// Hand-written Italian lessons.
// Lesson, Vocabulary and Pronunciation all read from here.
// Same shape as lessonsEnglish.js: lessonsItalian[interfaceLanguage][lessonSlug].
// Right now only the English-explanation set exists. Other interface languages fall back to it
// until their own sets are written.

const italianEnglish = {
    'hello-and-goodbye': {
        title: 'Hello and goodbye',
        topic: 'Greetings & introductions',
        duration: '5 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Hello and goodbye in Italian',
                body: 'Italians greet each other differently depending on the time of day and how well they know each other. Let\'s learn the essential greetings.',
                emoji: '👋',
            },
            {
                type: 'vocabulary',
                word: 'Ciao',
                translation: 'Hi / Bye (informal)',
                phonetic: '/tʃaʊ/',
                note: 'Used with friends and family. Never use ciao with a boss, teacher or stranger — it\'s too casual.',
                emoji: '👋',
            },
            {
                type: 'vocabulary',
                word: 'Buongiorno',
                translation: 'Good morning / Good day',
                phonetic: '/bwɔnˈdʒorno/',
                note: 'Used until early afternoon. Safe to use with anyone — formal or informal.',
                emoji: '🌅',
            },
            {
                type: 'vocabulary',
                word: 'Buonasera',
                translation: 'Good evening',
                phonetic: '/bwɔnaˈseːra/',
                note: 'Used from late afternoon onwards. Also a polite, formal greeting.',
                emoji: '🌆',
            },
            {
                type: 'vocabulary',
                word: 'Arrivederci',
                translation: 'Goodbye (formal)',
                phonetic: '/arriˈvederˌtʃi/',
                note: 'The standard formal goodbye. Literally means "until we see each other again".',
                emoji: '🤝',
            },
            {
                type: 'vocabulary',
                word: 'A presto',
                translation: 'See you soon',
                phonetic: '/a ˈpresto/',
                note: 'Used when you expect to see the person again soon. Warmer than arrivederci.',
                emoji: '😊',
            },
            {
                type: 'question',
                question: 'Which greeting is too casual to use with your Italian teacher?',
                options: ['Buongiorno', 'Salve', 'Ciao', 'Buonasera'],
                correct: 2,
                explanation: 'Ciao is too informal for teachers or strangers. Buongiorno, Salve and Buonasera are all appropriate.',
            },
            {
                type: 'question',
                question: 'What does "Arrivederci" literally mean?',
                options: ['Have a good day', 'Goodbye forever', 'Until we see each other again', 'Good evening'],
                correct: 2,
                explanation: 'Arrivederci comes from "arrivare" (to arrive) and "vederci" (to see each other) — until we see each other again.',
            },
            {
                type: 'question',
                question: 'It\'s 7pm. Which greeting is correct?',
                options: ['Buongiorno', 'Buonasera', 'Buonanotte', 'Ciao'],
                correct: 1,
                explanation: 'Buonasera is used from late afternoon onwards. Buongiorno is for the morning and early afternoon.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You\'ve learned the core Italian greetings and when to use them. Next up — how to introduce yourself.',
                xp: 15,
            },
        ],
    },
    'whats-your-name': {
        title: 'What\'s your name?',
        topic: 'Greetings & introductions',
        duration: '5 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Introducing yourself in Italian',
                body: 'One of the first things you\'ll do in any new language is say your name. In Italian there are two ways — formal and informal.',
                emoji: '🙋',
            },
            {
                type: 'vocabulary',
                word: 'Come ti chiami?',
                translation: 'What\'s your name? (informal)',
                phonetic: '/ˈkome ti ˈkjaːmi/',
                note: 'Use this with people your age or younger, or anyone you\'re on friendly terms with.',
                emoji: '💬',
            },
            {
                type: 'vocabulary',
                word: 'Come si chiama?',
                translation: 'What\'s your name? (formal)',
                phonetic: '/ˈkome si ˈkjaːma/',
                note: 'Use this with strangers, older people or in professional situations.',
                emoji: '👔',
            },
            {
                type: 'vocabulary',
                word: 'Mi chiamo...',
                translation: 'My name is...',
                phonetic: '/mi ˈkjaːmo/',
                note: 'Literally "I call myself". This is the standard way to give your name in Italian.',
                emoji: '🙋',
            },
            {
                type: 'vocabulary',
                word: 'Sono...',
                translation: 'I am...',
                phonetic: '/ˈsono/',
                note: 'A shorter, more casual way to introduce yourself. "Sono Andrea" — I\'m Andrea.',
                emoji: '😎',
            },
            {
                type: 'question',
                question: 'How do you ask someone\'s name formally?',
                options: ['Come ti chiami?', 'Chi sei?', 'Come si chiama?', 'Qual è il tuo nome?'],
                correct: 2,
                explanation: 'Come si chiama? is the formal version. Come ti chiami? is informal — use it with friends.',
            },
            {
                type: 'question',
                question: 'How do you say "My name is Marco"?',
                options: ['Io sono chiamato Marco', 'Mi chiamo Marco', 'Il mio nome Marco', 'Chiamo Marco'],
                correct: 1,
                explanation: 'Mi chiamo Marco is the natural way. Literally "I call myself Marco".',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now introduce yourself and ask someone\'s name in Italian. Next — where are you from?',
                xp: 15,
            },
        ],
    },
    'where-are-you-from': {
        title: 'Where are you from?',
        topic: 'Greetings & introductions',
        duration: '7 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'Talking about where you\'re from',
                body: 'After names, the next question is always where you\'re from. This lesson covers countries, cities and how to ask and answer naturally.',
                emoji: '🌍',
            },
            {
                type: 'vocabulary',
                word: 'Di dove sei?',
                translation: 'Where are you from? (informal)',
                phonetic: '/di ˈdove ˈsɛi/',
                note: 'The most common way to ask where someone is from in casual conversation.',
                emoji: '🌍',
            },
            {
                type: 'vocabulary',
                word: 'Sono di...',
                translation: 'I\'m from...',
                phonetic: '/ˈsono di/',
                note: 'Follow this with your city or country. "Sono di Milano." — I\'m from Milan.',
                emoji: '📍',
            },
            {
                type: 'vocabulary',
                word: 'Vengo da...',
                translation: 'I come from...',
                phonetic: '/ˈveŋɡo da/',
                note: 'Slightly more emphatic than "sono di". Both are correct and natural.',
                emoji: '✈️',
            },
            {
                type: 'vocabulary',
                word: 'l\'Inghilterra',
                translation: 'England',
                phonetic: '/liŋɡilˈtɛrra/',
                note: 'Vengo dall\'Inghilterra — I come from England. Note the "dall\'" contraction before vowels.',
                emoji: '🇬🇧',
            },
            {
                type: 'question',
                question: 'How do you say "I\'m from Rome"?',
                options: ['Io Roma', 'Sono a Roma', 'Sono di Roma', 'Vengo Roma'],
                correct: 2,
                explanation: 'Sono di Roma is correct. "Di" means from in this context — sono di + city.',
            },
            {
                type: 'question',
                question: 'What does "Di dove sei?" mean?',
                options: ['Where are you going?', 'Where are you from?', 'Where do you live?', 'Where were you born?'],
                correct: 1,
                explanation: 'Di dove sei? literally means "Of where are you?" — the natural way to ask where someone is from.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now ask and answer where you\'re from in Italian. Next — how are you?',
                xp: 20,
            },
        ],
    },
    'how-are-you': {
        title: 'How are you?',
        topic: 'Greetings & introductions',
        duration: '5 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Asking how someone is',
                body: 'After hello, the next step is asking how someone is. Italians have several ways to do this — and the responses matter too.',
                emoji: '😊',
            },
            {
                type: 'vocabulary',
                word: 'Come stai?',
                translation: 'How are you? (informal)',
                phonetic: '/ˈkome ˈstai/',
                note: 'Use with friends and people you know. The formal version is "Come sta?"',
                emoji: '😊',
            },
            {
                type: 'vocabulary',
                word: 'Bene, grazie',
                translation: 'Well, thank you',
                phonetic: '/ˈbɛne ˈɡrattsje/',
                note: 'The standard response. Always add "grazie" — it sounds much more natural.',
                emoji: '👍',
            },
            {
                type: 'vocabulary',
                word: 'Così così',
                translation: 'So so',
                phonetic: '/koˈzi koˈzi/',
                note: 'Used when things are neither good nor bad. A very Italian expression.',
                emoji: '🤷',
            },
            {
                type: 'vocabulary',
                word: 'Non c\'è male',
                translation: 'Not bad',
                phonetic: '/non tʃɛ ˈmaːle/',
                note: 'Literally "there is no bad". A common, casual response meaning things are fine.',
                emoji: '😌',
            },
            {
                type: 'question',
                question: 'How do you ask "How are you?" formally?',
                options: ['Come stai?', 'Come va?', 'Come sta?', 'Stai bene?'],
                correct: 2,
                explanation: 'Come sta? is the formal version. Come stai? is informal. Come va? works for both.',
            },
            {
                type: 'question',
                question: 'What does "così così" mean?',
                options: ['Very well', 'Not bad', 'So so', 'Terrible'],
                correct: 2,
                explanation: 'Così così means so so — things are neither great nor bad. A very common Italian expression.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You now know how to ask and respond to "how are you?" in Italian. One more lesson to go!',
                xp: 15,
            },
        ],
    },
    'nice-to-meet-you': {
        title: 'Nice to meet you',
        topic: 'Greetings & introductions',
        duration: '5 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Polite phrases when meeting someone',
                body: 'First impressions matter. These phrases will help you come across as polite and natural when meeting Italians for the first time.',
                emoji: '🤝',
            },
            {
                type: 'vocabulary',
                word: 'Piacere',
                translation: 'Nice to meet you / Pleased to meet you',
                phonetic: '/pjaˈtʃeːre/',
                note: 'The single most useful word when meeting someone. Can be used alone or as part of a longer phrase.',
                emoji: '🤝',
            },
            {
                type: 'vocabulary',
                word: 'Piacere di conoscerti',
                translation: 'Nice to meet you (informal)',
                phonetic: '/pjaˈtʃeːre di koˈnoʃʃerti/',
                note: 'More complete than just "piacere". Literally "pleasure to know you".',
                emoji: '😊',
            },
            {
                type: 'vocabulary',
                word: 'Anche io',
                translation: 'Me too / Likewise',
                phonetic: '/ˈaŋke ˈio/',
                note: 'The natural response when someone says piacere to you. "Piacere!" — "Anche io!"',
                emoji: '👐',
            },
            {
                type: 'vocabulary',
                word: 'È un piacere conoscerti',
                translation: 'It\'s a pleasure to meet you',
                phonetic: '/ɛ un pjaˈtʃeːre koˈnoʃʃerti/',
                note: 'A fuller, slightly more formal version. Great for professional introductions.',
                emoji: '✨',
            },
            {
                type: 'question',
                question: 'Someone says "Piacere!" to you. What do you reply?',
                options: ['Grazie', 'Anche io', 'Ciao', 'Bene'],
                correct: 1,
                explanation: 'Anche io means "me too" or "likewise" — the natural response to piacere.',
            },
            {
                type: 'question',
                question: 'What does "piacere" literally relate to?',
                options: ['Meeting', 'Pleasure', 'Greeting', 'Knowing'],
                correct: 1,
                explanation: 'Piacere means pleasure. So "piacere di conoscerti" literally means "pleasure to know you".',
            },
            {
                type: 'complete',
                title: 'Greetings & introductions — complete!',
                body: 'You\'ve finished all 5 lessons in this topic. You can now greet people, introduce yourself, say where you\'re from and be polite when meeting someone new in Italian.',
                xp: 15,
            },
        ],
    },
    'talking-about-your-job': {
        title: 'Talking about your job',
        topic: 'Greetings & introductions',
        duration: '7 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'What do you do?',
                body: 'After names and origins, talking about work is one of the most common topics in introductions. Let\'s learn how to ask about and describe jobs.',
                emoji: '💼',
            },
            {
                type: 'vocabulary',
                word: 'Che lavoro fai?',
                translation: 'What do you do for work? (informal)',
                phonetic: '/ke laˈvoːro ˈfai/',
                note: 'The natural, casual way to ask about someone\'s job. Formal version is "Che lavoro fa?"',
                emoji: '💼',
            },
            {
                type: 'vocabulary',
                word: 'Faccio l\'insegnante',
                translation: 'I am a teacher (I do the teacher)',
                phonetic: '/ˈfattʃo linseɲˈɲante/',
                note: 'Italian uses "fare" (to do) + article + profession, unlike English "I am a...".',
                emoji: '👩‍🏫',
            },
            {
                type: 'vocabulary',
                word: 'Sono medico',
                translation: 'I am a doctor',
                phonetic: '/ˈsono ˈmɛːdiko/',
                note: 'With "essere" (to be), no article is needed before the profession — "sono medico", not "sono un medico".',
                emoji: '👨‍⚕️',
            },
            {
                type: 'vocabulary',
                word: 'Lavoro in un ufficio',
                translation: 'I work in an office',
                phonetic: '/laˈvoːro in un ufˈfitʃo/',
                note: 'Useful when you don\'t have a specific job title to mention, or want to describe where you work.',
                emoji: '🏢',
            },
            {
                type: 'vocabulary',
                word: 'Sono disoccupato / disoccupata',
                translation: 'I am unemployed (male/female)',
                phonetic: '/ˈsono dizokkuˈpaːto/',
                note: 'A neutral, common way to describe being between jobs.',
                emoji: '🔍',
            },
            {
                type: 'vocabulary',
                word: 'Studio all\'università',
                translation: 'I study at university',
                phonetic: '/ˈstuːdjo allunivɛrsiˈta/',
                note: 'Useful if you\'re a student rather than working — "studio" from "studiare" (to study).',
                emoji: '🎓',
            },
            {
                type: 'question',
                question: 'How do you ask "What do you do for work?" informally?',
                options: ['Che lavoro fa?', 'Che lavoro fai?', 'Dove lavori?', 'Sei disoccupato?'],
                correct: 1,
                explanation: '"Che lavoro fai?" is the informal version. "Che lavoro fa?" is used formally.',
            },
            {
                type: 'question',
                question: 'How would a doctor introduce their profession using "essere"?',
                options: ['Sono un medico', 'Sono medico', 'Faccio il medico', 'Ho un medico'],
                correct: 1,
                explanation: '"Sono medico" is correct — no article is needed before a profession with "essere".',
            },
            {
                type: 'question',
                question: 'What does "faccio l\'insegnante" mean?',
                options: ['I study to be a teacher', 'I am a teacher', 'I know a teacher', 'I was a teacher'],
                correct: 1,
                explanation: '"Fare" + article + profession is a common pattern for describing your job — "faccio l\'insegnante" means I am a teacher.',
            },
            {
                type: 'question',
                question: 'What does "studio all\'università" mean?',
                options: ['I teach at university', 'I study at university', 'I work near a university', 'I graduated from university'],
                correct: 1,
                explanation: '"Studio all\'università" means I study at university — from the verb "studiare".',
            },
            {
                type: 'question',
                question: 'How would you say "I am unemployed" as a woman?',
                options: ['Sono disoccupato', 'Sono disoccupata', 'Faccio disoccupata', 'Ho disoccupata'],
                correct: 1,
                explanation: '"Sono disoccupata" is the feminine form — matching the "-a" ending to the speaker\'s gender.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now talk about your job and ask others about theirs in Italian. Next up — asking someone\'s age.',
                xp: 20,
            },
        ],
    },
    'asking-someones-age': {
        title: 'Asking someone\'s age',
        topic: 'Greetings & introductions',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'How old are you?',
                body: 'Age comes up naturally in conversation, especially with people you\'re just getting to know or with children. Let\'s learn how to ask and answer.',
                emoji: '🎂',
            },
            {
                type: 'vocabulary',
                word: 'Quanti anni hai?',
                translation: 'How old are you? (informal)',
                phonetic: '/ˈkwanti ˈanni ˈai/',
                note: 'Literally "how many years do you have?" — Italian always uses "avere" (to have) for age.',
                emoji: '❓',
            },
            {
                type: 'vocabulary',
                word: 'Quanti anni ha?',
                translation: 'How old are you? (formal)',
                phonetic: '/ˈkwanti ˈanni a/',
                note: 'The formal version — used with strangers or older people, especially in professional contexts.',
                emoji: '🎩',
            },
            {
                type: 'vocabulary',
                word: 'Ho ventotto anni',
                translation: 'I am twenty-eight years old',
                phonetic: '/ɔ venˈtɔtto ˈanni/',
                note: '"Ho" (I have) + number + "anni" — never use "sono" (I am) for age in Italian.',
                emoji: '🔢',
            },
            {
                type: 'vocabulary',
                word: 'Compio gli anni a marzo',
                translation: 'My birthday is in March (I turn a year older in March)',
                phonetic: '/ˈkompjo ʎi ˈanni a ˈmartso/',
                note: 'From "compiere gli anni" — literally "to complete the years". A natural way to mention your birthday month.',
                emoji: '🎉',
            },
            {
                type: 'vocabulary',
                word: 'Non è educato chiedere l\'età',
                translation: 'It\'s not polite to ask someone\'s age',
                phonetic: '/non ɛ edukaːto kjeˈdeːre leˈta/',
                note: 'A useful cultural note — asking age can feel intrusive with some people, especially adults you don\'t know well.',
                emoji: '🤫',
            },
            {
                type: 'vocabulary',
                word: 'L\'età',
                translation: 'The age',
                phonetic: '/leˈta/',
                note: 'The noun form of age, used in phrases like "la mia età" (my age).',
                emoji: '📆',
            },
            {
                type: 'question',
                question: 'How is age expressed in Italian?',
                options: ['With "essere" (to be)', 'With "avere" (to have)', 'With "fare" (to do)', 'With "stare" (to stay)'],
                correct: 1,
                explanation: 'Italian always uses "avere" for age — "ho ventotto anni" literally means "I have twenty-eight years".',
            },
            {
                type: 'question',
                question: 'How would you ask "How old are you?" formally?',
                options: ['Quanti anni hai?', 'Quanti anni ha?', 'Che età fai?', 'Sei vecchio?'],
                correct: 1,
                explanation: '"Quanti anni ha?" is the formal version, used with strangers or in professional contexts.',
            },
            {
                type: 'question',
                question: 'What does "compio gli anni a marzo" mean?',
                options: ['I was born in March', 'My birthday is in March', 'I visited in March', 'I work in March'],
                correct: 1,
                explanation: '"Compiere gli anni" literally means "to complete the years" — a natural way to talk about your birthday.',
            },
            {
                type: 'question',
                question: 'Why might you avoid asking someone\'s age directly?',
                options: ['It\'s grammatically incorrect', 'It can feel intrusive or impolite, especially with adults you don\'t know well', 'It\'s only used for children', 'It\'s a formal-only phrase'],
                correct: 1,
                explanation: 'Asking age can feel intrusive in many cultures, including Italy — it\'s a useful cultural awareness point.',
            },
            {
                type: 'question',
                question: 'What is "l\'età"?',
                options: ['A birthday party', 'The noun for "age"', 'A type of greeting', 'A question word'],
                correct: 1,
                explanation: '"L\'età" is the noun form of age, used in phrases like "la mia età" (my age).',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now ask about and share your age naturally, with an awareness of when it\'s appropriate to ask. Next up — saying please and thank you.',
                xp: 15,
            },
        ],
    },
    'saying-please-and-thank-you': {
        title: 'Saying please and thank you',
        topic: 'Greetings & introductions',
        duration: '5 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Everyday politeness',
                body: 'Small polite words matter enormously in Italian — using them naturally makes you sound warm and considerate. Let\'s master the essentials.',
                emoji: '🙏',
            },
            {
                type: 'vocabulary',
                word: 'Per favore',
                translation: 'Please',
                phonetic: '/per faˈvoːre/',
                note: 'The standard way to say please — used constantly in requests, big or small.',
                emoji: '🙏',
            },
            {
                type: 'vocabulary',
                word: 'Grazie',
                translation: 'Thank you',
                phonetic: '/ˈɡrattsje/',
                note: 'One of the most important words in any language — used far more often than in English.',
                emoji: '😊',
            },
            {
                type: 'vocabulary',
                word: 'Grazie mille',
                translation: 'Thank you very much (a thousand thanks)',
                phonetic: '/ˈɡrattsje ˈmille/',
                note: 'Literally "a thousand thanks" — a warmer, more emphatic way to say thank you.',
                emoji: '💐',
            },
            {
                type: 'vocabulary',
                word: 'Prego',
                translation: 'You\'re welcome / Please (go ahead)',
                phonetic: '/ˈprɛːɡo/',
                note: 'A flexible word — responds to "grazie" but also means "please, go ahead" when offering something.',
                emoji: '🤲',
            },
            {
                type: 'vocabulary',
                word: 'Non c\'è di che',
                translation: 'You\'re welcome / Don\'t mention it',
                phonetic: '/non tʃɛ di ke/',
                note: 'A casual alternative to "prego" — literally "there\'s nothing to it".',
                emoji: '😌',
            },
            {
                type: 'vocabulary',
                word: 'Ti ringrazio',
                translation: 'I thank you',
                phonetic: '/ti riŋɡraˈtsjo/',
                note: 'A more direct, personal way to say thank you than just "grazie" — used in slightly more formal or heartfelt moments.',
                emoji: '🙏',
            },
            {
                type: 'vocabulary',
                word: 'Molto gentile',
                translation: 'Very kind (of you)',
                phonetic: '/ˈmolto dʒenˈtiːle/',
                note: 'A warm response to someone doing something nice for you — "sei molto gentile" (you\'re very kind).',
                emoji: '💛',
            },
            {
                type: 'question',
                question: 'How do you say "thank you very much"?',
                options: ['Grazie prego', 'Grazie mille', 'Per favore grazie', 'Molto grazie'],
                correct: 1,
                explanation: '"Grazie mille" literally means "a thousand thanks" — a warm, emphatic way to say thank you very much.',
            },
            {
                type: 'question',
                question: 'What does "prego" mean when someone says "grazie" to you?',
                options: ['Please', 'You\'re welcome', 'Thank you too', 'Excuse me'],
                correct: 1,
                explanation: '"Prego" is the standard response to "grazie" — meaning "you\'re welcome".',
            },
            {
                type: 'question',
                question: 'What else can "prego" mean, besides "you\'re welcome"?',
                options: ['Goodbye', 'Please, go ahead (when offering something)', 'I\'m sorry', 'See you soon'],
                correct: 1,
                explanation: '"Prego" is flexible — it can also mean "please, go ahead" when inviting someone to do something.',
            },
            {
                type: 'question',
                question: 'What does "non c\'è di che" mean?',
                options: ['Please', 'You\'re welcome / don\'t mention it', 'Thank you', 'Excuse me'],
                correct: 1,
                explanation: '"Non c\'è di che" is a casual alternative to "prego" — literally "there\'s nothing to it".',
            },
            {
                type: 'question',
                question: 'What does "ti ringrazio" mean?',
                options: ['You\'re welcome', 'I thank you', 'Please help me', 'I\'m very kind'],
                correct: 1,
                explanation: '"Ti ringrazio" is a more direct, personal way to say thank you than the simple "grazie".',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You now know how to be politely appreciative in Italian, in both casual and warmer situations. Next up — apologizing and excusing yourself.',
                xp: 15,
            },
        ],
    },
    'apologizing-and-excusing-yourself': {
        title: 'Apologizing and excusing yourself',
        topic: 'Greetings & introductions',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Scusa vs mi dispiace',
                body: 'Italian has two main ways to apologize, and they\'re used differently. Let\'s learn when to use each one, plus a few other useful polite phrases.',
                emoji: '🙇',
            },
            {
                type: 'vocabulary',
                word: 'Scusa',
                translation: 'Excuse me / Sorry (informal)',
                phonetic: '/ˈskuːza/',
                note: 'Used to get attention, squeeze past someone, or for minor things. Formal version is "Scusi".',
                emoji: '🙋',
            },
            {
                type: 'vocabulary',
                word: 'Scusi',
                translation: 'Excuse me / Sorry (formal)',
                phonetic: '/ˈskuːzi/',
                note: 'Use with strangers, older people, or in professional settings — same uses as "scusa" but polite.',
                emoji: '🎩',
            },
            {
                type: 'vocabulary',
                word: 'Mi dispiace',
                translation: 'I\'m sorry (I regret)',
                phonetic: '/mi disˈpjaːtʃe/',
                note: 'Used for genuine regret or sympathy — deeper than "scusa". "Mi dispiace per il ritardo" (Sorry for the delay).',
                emoji: '😔',
            },
            {
                type: 'vocabulary',
                word: 'Scusa il ritardo',
                translation: 'Sorry for being late',
                phonetic: '/ˈskuːza il riˈtardo/',
                note: 'A very common everyday phrase — "il ritardo" means the delay/lateness.',
                emoji: '⏰',
            },
            {
                type: 'vocabulary',
                word: 'Permesso',
                translation: 'Excuse me (to pass by / enter)',
                phonetic: '/perˈmesso/',
                note: 'Specifically used when physically moving past someone or entering a space — different from "scusa".',
                emoji: '🚪',
            },
            {
                type: 'vocabulary',
                word: 'Non fa niente',
                translation: 'It\'s okay / No problem (it\'s nothing)',
                phonetic: '/non fa ˈnjɛnte/',
                note: 'The natural response when someone apologizes to you — literally "it does nothing".',
                emoji: '👍',
            },
            {
                type: 'vocabulary',
                word: 'Non importa',
                translation: 'It doesn\'t matter',
                phonetic: '/non imˈporta/',
                note: 'Another common way to reassure someone after they apologize.',
                emoji: '🤷',
            },
            {
                type: 'question',
                question: 'What\'s the difference between "scusa" and "mi dispiace"?',
                options: ['No difference, fully interchangeable', '"Scusa" is for minor things/getting attention; "mi dispiace" is for genuine regret', '"Scusa" is formal, "mi dispiace" is informal', '"Mi dispiace" is only used with strangers'],
                correct: 1,
                explanation: '"Scusa" is lighter — used for minor things or getting attention. "Mi dispiace" expresses deeper regret or sympathy.',
            },
            {
                type: 'question',
                question: 'When would you use "permesso"?',
                options: ['When apologizing for a mistake', 'When physically passing by or entering a space', 'When saying goodbye', 'When thanking someone'],
                correct: 1,
                explanation: '"Permesso" is specifically used when physically moving past someone or entering somewhere — different from a verbal apology.',
            },
            {
                type: 'question',
                question: 'What does "non fa niente" mean?',
                options: ['I\'m sorry', 'It\'s okay / no problem', 'Excuse me', 'I don\'t know'],
                correct: 1,
                explanation: '"Non fa niente" literally means "it does nothing" — the natural response when someone apologizes.',
            },
            {
                type: 'question',
                question: 'How would you formally say "excuse me" to a stranger?',
                options: ['Scusa', 'Scusi', 'Permesso', 'Mi dispiace'],
                correct: 1,
                explanation: '"Scusi" is the formal version of "scusa" — used with strangers or in professional contexts.',
            },
            {
                type: 'question',
                question: 'What does "scusa il ritardo" mean?',
                options: ['Sorry for being late', 'Sorry to bother you', 'Excuse me, may I pass?', 'I\'m sorry for the mistake'],
                correct: 0,
                explanation: '"Scusa il ritardo" means sorry for being late — "il ritardo" means the delay/lateness.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now apologize appropriately in different situations and respond graciously when others apologize to you. Next up — introducing someone else.',
                xp: 15,
            },
        ],
    },
    'introducing-someone-else': {
        title: 'Introducing someone else',
        topic: 'Greetings & introductions',
        duration: '6 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'This is my friend...',
                body: 'Beyond introducing yourself, you\'ll often need to introduce other people — friends, family, colleagues. Let\'s learn the natural phrases.',
                emoji: '🤝',
            },
            {
                type: 'vocabulary',
                word: 'Ti presento...',
                translation: 'Let me introduce you to... (informal)',
                phonetic: '/ti preˈzɛnto/',
                note: 'Literally "I present to you" — the standard informal way to introduce someone.',
                emoji: '👋',
            },
            {
                type: 'vocabulary',
                word: 'Le presento...',
                translation: 'Let me introduce you to... (formal)',
                phonetic: '/le preˈzɛnto/',
                note: 'The formal version — used when introducing someone to a person you address formally.',
                emoji: '🎩',
            },
            {
                type: 'vocabulary',
                word: 'Questo è il mio amico',
                translation: 'This is my friend (male)',
                phonetic: '/ˈkwesto ɛ il ˈmiːo aˈmiːko/',
                note: '"Questa è la mia amica" for a female friend — remember the gender agreement throughout.',
                emoji: '🧑',
            },
            {
                type: 'vocabulary',
                word: 'Vi conoscete già?',
                translation: 'Do you two already know each other?',
                phonetic: '/vi konoˈʃete dʒa/',
                note: 'A natural question to ask before introducing two people, to avoid unnecessary introductions.',
                emoji: '❓',
            },
            {
                type: 'vocabulary',
                word: 'Piacere di conoscerti',
                translation: 'Nice to meet you (response, informal)',
                phonetic: '/pjaˈtʃeːre di koˈnoʃʃerti/',
                note: 'What the newly introduced person says back — reviewed from an earlier lesson, now in context.',
                emoji: '😊',
            },
            {
                type: 'vocabulary',
                word: 'Lavoriamo insieme',
                translation: 'We work together',
                phonetic: '/lavoˈrjaːmo inˈsjɛme/',
                note: 'Useful context to add when introducing a colleague.',
                emoji: '💼',
            },
            {
                type: 'vocabulary',
                word: 'Ci conosciamo da...',
                translation: 'We\'ve known each other since / for...',
                phonetic: '/tʃi konoˈʃaːmo da/',
                note: 'Follow with a time period or event — "ci conosciamo dall\'università" (we\'ve known each other since university).',
                emoji: '⏳',
            },
            {
                type: 'question',
                question: 'How would you informally introduce a friend?',
                options: ['Le presento...', 'Ti presento...', 'Sono presento...', 'Mi presento...'],
                correct: 1,
                explanation: '"Ti presento..." is the informal way to introduce someone. "Le presento..." is the formal version.',
            },
            {
                type: 'question',
                question: 'How would you say "this is my friend" about a female friend?',
                options: ['Questo è il mio amico', 'Questa è la mia amica', 'Questo è la mia amica', 'Questa è il mio amico'],
                correct: 1,
                explanation: '"Questa è la mia amica" is correct — "questa", "la" and "amica" all agree in feminine form.',
            },
            {
                type: 'question',
                question: 'What does "vi conoscete già?" mean?',
                options: ['Do you know me?', 'Do you two already know each other?', 'Where do you live?', 'Are you friends?'],
                correct: 1,
                explanation: '"Vi conoscete già?" is a natural question to ask before introducing two people.',
            },
            {
                type: 'question',
                question: 'What would you say to introduce a formal contact\'s acquaintance?',
                options: ['Ti presento...', 'Le presento...', 'Mi presento...', 'Ci presento...'],
                correct: 1,
                explanation: '"Le presento..." is the formal version, used when introducing someone in a formal context.',
            },
            {
                type: 'question',
                question: 'What does "ci conosciamo dall\'università" mean?',
                options: ['We study at university', 'We\'ve known each other since university', 'We work at a university', 'We are visiting a university'],
                correct: 1,
                explanation: '"Ci conosciamo da..." followed by a time reference describes how long you\'ve known someone.',
            },
            {
                type: 'complete',
                title: 'Greetings & introductions — complete!',
                body: 'You\'ve finished all 10 lessons in this topic! You can now greet people, introduce yourself and others, ask about jobs and age, and handle everyday politeness in Italian.',
                xp: 20,
            },
        ],
    },
    'types-of-coffee': {
        title: 'Types of coffee',
        topic: 'At the cafe',
        duration: '5 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Types of coffee in Italy',
                body: 'In Italy, coffee is serious business. Each drink has a specific name, size and time of day when it\'s acceptable to order it. Let\'s learn the most important ones.',
                emoji: '☕',
            },
            {
                type: 'vocabulary',
                word: 'il caffè',
                translation: 'espresso / coffee',
                phonetic: '/il kafˈfɛ/',
                note: 'In Italy "caffè" always means a small, strong espresso — never a large milky drink.',
                emoji: '☕',
            },
            {
                type: 'vocabulary',
                word: 'il cappuccino',
                translation: 'cappuccino',
                phonetic: '/il kapputˈtʃiːno/',
                note: 'Only drunk at breakfast in Italy — never after a meal. Ordering one after lunch marks you as a tourist immediately.',
                emoji: '🥛',
            },
            {
                type: 'vocabulary',
                word: 'il macchiato',
                translation: 'espresso with a dash of milk',
                phonetic: '/il makˈkjaːto/',
                note: 'Literally means "stained" — an espresso stained with a small amount of milk foam.',
                emoji: '🫙',
            },
            {
                type: 'vocabulary',
                word: 'il ristretto',
                translation: 'short strong espresso',
                phonetic: '/il riˈstretto/',
                note: 'Even shorter and stronger than a normal espresso. Beloved by serious coffee drinkers.',
                emoji: '⚡',
            },
            {
                type: 'question',
                question: 'What does "caffè" mean in Italy?',
                options: ['A large milky coffee', 'A small strong espresso', 'A cappuccino', 'A cold coffee'],
                correct: 1,
                explanation: 'In Italy, caffè always refers to a small, strong espresso shot — not a large or milky drink.',
            },
            {
                type: 'question',
                question: 'When do Italians drink cappuccino?',
                options: ['After dinner', 'Any time of day', 'Only at breakfast', 'Only in the afternoon'],
                correct: 2,
                explanation: 'Cappuccino is a breakfast drink in Italy. Ordering one after a meal is considered unusual.',
            },
            {
                type: 'question',
                question: 'What does "macchiato" literally mean?',
                options: ['Strong', 'Stained', 'Small', 'Sweet'],
                correct: 1,
                explanation: 'Macchiato means "stained" — an espresso stained with a small amount of milk foam.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You\'ve learned 4 types of Italian coffee and when to order them. Next up — how to actually order at the bar.',
                xp: 15,
            },
        ],
    },
    'numbers-0-10': {
        title: 'Numbers 0-10',
        topic: 'Numbers & counting',
        duration: '8 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'Counting from zero to ten',
                body: 'Numbers are everywhere — prices, ages, phone numbers, addresses. Let\'s start with the basics: zero through ten.',
                emoji: '🔢',
            },
            { type: 'vocabulary', word: 'Zero', translation: '0 — zero', phonetic: '/ˈdzɛːro/', note: 'Used the same way as in English — for nothing, or as a starting point.', emoji: '0️⃣' },
            { type: 'vocabulary', word: 'Uno', translation: '1 — one', phonetic: '/ˈuːno/', note: 'Also doubles as the indefinite article "a/an" in Italian — un caffè (a coffee).', emoji: '1️⃣' },
            { type: 'vocabulary', word: 'Due', translation: '2 — two', phonetic: '/ˈduːe/', note: 'One of the easiest numbers to remember — short and simple.', emoji: '2️⃣' },
            { type: 'vocabulary', word: 'Tre', translation: '3 — three', phonetic: '/tre/', note: 'Note the accent shifts when combined: ventitré (23) keeps the accent on the è.', emoji: '3️⃣' },
            { type: 'vocabulary', word: 'Quattro', translation: '4 — four', phonetic: '/ˈkwattro/', note: 'A double consonant — make sure to pronounce both T sounds.', emoji: '4️⃣' },
            { type: 'vocabulary', word: 'Cinque', translation: '5 — five', phonetic: '/ˈtʃiŋkwe/', note: 'The "c" before "i" makes a "ch" sound, like in "cheese".', emoji: '5️⃣' },
            { type: 'vocabulary', word: 'Sei', translation: '6 — six', phonetic: '/ˈsɛi/', note: 'Also means "you are" (from essere) — context tells you which meaning is intended.', emoji: '6️⃣' },
            { type: 'vocabulary', word: 'Sette', translation: '7 — seven', phonetic: '/ˈsɛtte/', note: 'Double T again — a common pattern in Italian numbers.', emoji: '7️⃣' },
            { type: 'vocabulary', word: 'Otto', translation: '8 — eight', phonetic: '/ˈɔtto/', note: 'Short and easy to remember — rhymes with "motto".', emoji: '8️⃣' },
            { type: 'vocabulary', word: 'Nove', translation: '9 — nine', phonetic: '/ˈnɔːve/', note: 'Don\'t confuse with "nuove" (new, feminine plural) — similar sound, different word.', emoji: '9️⃣' },
            { type: 'vocabulary', word: 'Dieci', translation: '10 — ten', phonetic: '/ˈdjɛːtʃi/', note: 'The base for all the teen numbers you\'ll learn next — undici, dodici and so on.', emoji: '🔟' },
            {
                type: 'question',
                question: 'How do you say "5" in Italian?',
                options: ['Quattro', 'Cinque', 'Sei', 'Sette'],
                correct: 1,
                explanation: 'Cinque means 5. The "c" before "i" sounds like "ch" in English.',
            },
            {
                type: 'question',
                question: 'What number is "otto"?',
                options: ['6', '7', '8', '9'],
                correct: 2,
                explanation: 'Otto means 8. Short and rhymes with "motto".',
            },
            {
                type: 'question',
                question: 'Which word also means "you are" in Italian?',
                options: ['Due', 'Sei', 'Nove', 'Dieci'],
                correct: 1,
                explanation: 'Sei means both "6" and "you are" (from the verb essere) — context makes the meaning clear.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now count from zero to ten in Italian. Next up — the numbers from 11 to 20.',
                xp: 20,
            },
        ],
    },
    'numbers-11-20': {
        title: 'Numbers 11-20',
        topic: 'Numbers & counting',
        duration: '7 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'The teens — eleven to twenty',
                body: 'These numbers don\'t follow a clean pattern at first, so we\'ll learn each one individually. Pay attention — from 17 onward a pattern starts to emerge.',
                emoji: '🔢',
            },
            { type: 'vocabulary', word: 'Undici', translation: '11 — eleven', phonetic: '/ˈunditʃi/', note: 'Irregular — doesn\'t clearly combine "dieci" and "uno". Just memorize it.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Dodici', translation: '12 — twelve', phonetic: '/ˈdoditʃi/', note: 'Comes from "due" + "dieci", shortened in everyday speech.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Tredici', translation: '13 — thirteen', phonetic: '/ˈtreditʃi/', note: 'From "tre" + "dieci" — you can start to hear where it comes from.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Quattordici', translation: '14 — fourteen', phonetic: '/kwatˈtorditʃi/', note: 'From "quattro" + "dieci". Take your time with the longer numbers.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Quindici', translation: '15 — fifteen', phonetic: '/ˈkwinditʃi/', note: 'A small irregularity — quindici, not "cinquedici". Just memorize it.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Sedici', translation: '16 — sixteen', phonetic: '/ˈseditʃi/', note: 'From "sei" + "dieci", shortened. The last of the fully irregular teens.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Diciassette', translation: '17 — seventeen', phonetic: '/ditʃasˈsɛtte/', note: 'Notice the pattern shift here — it\'s "dici" (from dieci) + "assette" (sette). This same pattern continues for 18 and 19.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Diciotto', translation: '18 — eighteen', phonetic: '/diˈtʃɔtto/', note: '"Dici" + "otto" — same pattern as diciassette, just with otto instead.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Diciannove', translation: '19 — nineteen', phonetic: '/ditʃanˈnɔːve/', note: '"Dici" + "annove" (nove) — the pattern holds one more time.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Venti', translation: '20 — twenty', phonetic: '/ˈventi/', note: 'A clean break from the teens — this is the first of the "tens" you\'ll build on in the next lesson.', emoji: '🔢' },
            {
                type: 'question',
                question: 'How do you say "14" in Italian?',
                options: ['Quindici', 'Quattordici', 'Quattro', 'Quaranta'],
                correct: 1,
                explanation: 'Quattordici means 14, from "quattro" + "dieci".',
            },
            {
                type: 'question',
                question: 'Which number is "sedici"?',
                options: ['15', '16', '17', '18'],
                correct: 1,
                explanation: 'Sedici means 16 — the last of the fully irregular teen numbers.',
            },
            {
                type: 'question',
                question: 'What pattern do diciassette, diciotto and diciannove follow?',
                options: ['No pattern — all irregular', '"Dieci" (shortened to "dici") + the unit number', '"Venti" + the unit number', 'They\'re identical to 7, 8, 9'],
                correct: 1,
                explanation: 'From 17 onward, the teens follow "dieci" (shortened to "dici-") plus the unit number — diciassette is "dici" + "sette".',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You now know every number from 11 to 20. Next up — combining venti with the units to make 21 through 29.',
                xp: 20,
            },
        ],
    },
    'numbers-21-29': {
        title: 'Numbers 21-29',
        topic: 'Numbers & counting',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Building the twenties',
                body: 'Now that you know venti (20), you can build every number from 21 to 29 using one simple rule: combine venti with the unit number, with a couple of small tweaks.',
                emoji: '🔢',
            },
            { type: 'vocabulary', word: 'Ventuno', translation: '21 — twenty-one', phonetic: '/venˈtuːno/', note: 'Venti + uno, but the final "i" of venti drops before uno: vent + uno = ventuno.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Ventidue', translation: '22 — twenty-two', phonetic: '/ventiˈdue/', note: 'Here nothing drops — venti + due simply joins together.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Ventitré', translation: '23 — twenty-three', phonetic: '/ventiˈtre/', note: 'Notice tre keeps its accent mark even when combined — ventitré, not ventitre.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Ventiquattro', translation: '24 — twenty-four', phonetic: '/ventiˈkwattro/', note: 'Venti + quattro, joined with no changes.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Venticinque', translation: '25 — twenty-five', phonetic: '/ventiˈtʃiŋkwe/', note: 'Venti + cinque, joined with no changes.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Ventisei', translation: '26 — twenty-six', phonetic: '/ventiˈsɛi/', note: 'Venti + sei, joined with no changes.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Ventisette', translation: '27 — twenty-seven', phonetic: '/ventiˈsɛtte/', note: 'Venti + sette, joined with no changes.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Ventotto', translation: '28 — twenty-eight', phonetic: '/venˈtɔtto/', note: 'Same drop as ventuno — the final "i" of venti drops before otto too: vent + otto = ventotto.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Ventinove', translation: '29 — twenty-nine', phonetic: '/ventiˈnɔːve/', note: 'Venti + nove, joined with no changes.', emoji: '🔢' },
            {
                type: 'question',
                question: 'How do you say "23" correctly?',
                options: ['Ventitre (no accent)', 'Ventitré (with accent on é)', 'Venti tre (two words)', 'Tre venti'],
                correct: 1,
                explanation: 'Ventitré keeps the accent on the é from "tré" — this is one of the few numbers where the accent matters.',
            },
            {
                type: 'question',
                question: 'What happens to "venti" before "uno" or "otto"?',
                options: ['Nothing changes', 'The final "i" of venti drops', 'The whole word doubles', 'A space is added'],
                correct: 1,
                explanation: 'Before uno and otto specifically, the final "i" of venti drops — ventuno and ventotto, not "ventiuno" or "ventiotto".',
            },
            {
                type: 'question',
                question: 'How do you say "28"?',
                options: ['Ventiotto', 'Ventotto', 'Vintiotto', 'Ottoventi'],
                correct: 1,
                explanation: 'Ventotto is correct — the final "i" of venti drops before otto, just like it does before uno.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now count from 21 to 29. This exact same combining rule applies to every set of tens — next up, thirty through one hundred.',
                xp: 15,
            },
        ],
    },
    'numbers-30-100': {
        title: 'Numbers 30-100',
        topic: 'Numbers & counting',
        duration: '7 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'Thirty to one hundred',
                body: 'Good news — from here, the pattern is completely predictable. Learn these 7 "ten" words, and you can combine them with any unit exactly the way you just did with venti.',
                emoji: '🔢',
            },
            { type: 'vocabulary', word: 'Trenta', translation: '30 — thirty', phonetic: '/ˈtrenta/', note: 'The first of the predictable tens — no irregularities from here on.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Quaranta', translation: '40 — forty', phonetic: '/kwaˈranta/', note: 'Same family as trenta — just swap the start.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Cinquanta', translation: '50 — fifty', phonetic: '/tʃiŋˈkwanta/', note: 'From "cinque" — you can hear the connection.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Sessanta', translation: '60 — sixty', phonetic: '/sesˈsanta/', note: 'From "sei" — double S in the middle.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Settanta', translation: '70 — seventy', phonetic: '/setˈtanta/', note: 'From "sette" — double T in the middle.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Ottanta', translation: '80 — eighty', phonetic: '/otˈtanta/', note: 'From "otto" — double T again.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Novanta', translation: '90 — ninety', phonetic: '/noˈvanta/', note: 'From "nove" — the last of the tens before cento.', emoji: '🔢' },
            { type: 'vocabulary', word: 'Cento', translation: '100 — one hundred', phonetic: '/ˈtʃɛnto/', note: 'No "un" needed before it, unlike English "one hundred" — just "cento" on its own.', emoji: '💯' },
            {
                type: 'vocabulary',
                word: 'Trentuno, quarantadue...',
                translation: '31, 42 — the same rule applies',
                phonetic: '/trenˈtuːno, kwarantaˈdue/',
                note: 'Exactly the same combining rule as venti: drop the final vowel before uno/otto (trentuno), otherwise just join the words (quarantadue).',
                emoji: '🔢',
            },
            {
                type: 'question',
                question: 'How do you say "60" in Italian?',
                options: ['Sei', 'Sessanta', 'Sedici', 'Settanta'],
                correct: 1,
                explanation: 'Sessanta means 60. Easy to confuse with sedici (16) or settanta (70) — listen carefully.',
            },
            {
                type: 'question',
                question: 'How would you write "82" as one word?',
                options: ['Ottanta due', 'Ottantadue', 'Ottodue', 'Ottanta e due'],
                correct: 1,
                explanation: 'Ottantadue — joined as one word with no space, following the same rule you learned with the twenties.',
            },
            {
                type: 'question',
                question: 'Do you say "un cento" for 100, like English "one hundred"?',
                options: ['Yes, always', 'No, just "cento"', 'Only above 200', 'Only in writing'],
                correct: 1,
                explanation: 'Italian just says "cento" — no "un" needed, unlike English.',
            },
            {
                type: 'complete',
                title: 'Numbers up to 100 — complete!',
                body: 'You can now count confidently from zero all the way to one hundred. Next up — telling the time.',
                xp: 20,
            },
        ],
    },
    'telling-the-time': {
        title: 'Telling the time',
        topic: 'Numbers & counting',
        duration: '7 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'What time is it?',
                body: 'Telling the time uses the numbers you\'ve just learned, plus a few special phrases. Italian has one quirky exception — let\'s start there.',
                emoji: '🕐',
            },
            { type: 'vocabulary', word: 'Che ore sono?', translation: 'What time is it?', phonetic: '/ke ˈoːre ˈsono/', note: 'Literally "what hours are they" — time is plural in Italian.', emoji: '🕐' },
            { type: 'vocabulary', word: 'Sono le...', translation: 'It\'s ... o\'clock', phonetic: '/ˈsono le/', note: 'Used for every hour except one. "Sono le tre" — it\'s three o\'clock.', emoji: '🕒' },
            { type: 'vocabulary', word: 'È l\'una', translation: 'It\'s one o\'clock', phonetic: '/ɛ ˈluːna/', note: 'The one exception — "una" is singular, so it takes "è" instead of "sono le".', emoji: '🕐' },
            { type: 'vocabulary', word: 'e mezza', translation: 'half past', phonetic: '/e ˈmɛddza/', note: 'Added after the hour — "Sono le tre e mezza" — it\'s half past three.', emoji: '🕞' },
            { type: 'vocabulary', word: 'e un quarto', translation: 'quarter past', phonetic: '/e un ˈkwarto/', note: '"Sono le quattro e un quarto" — it\'s quarter past four.', emoji: '🕟' },
            { type: 'vocabulary', word: 'meno un quarto', translation: 'quarter to', phonetic: '/ˈmeno un ˈkwarto/', note: '"Sono le cinque meno un quarto" — it\'s quarter to five. Literally "five minus a quarter".', emoji: '🕜' },
            {
                type: 'question',
                question: 'How do you say "It\'s one o\'clock"?',
                options: ['Sono le una', 'È l\'una', 'Sono l\'una', 'È le una'],
                correct: 1,
                explanation: 'È l\'una is the only exception — one o\'clock is singular, so it uses "è" not "sono le".',
            },
            {
                type: 'question',
                question: 'What does "e mezza" mean?',
                options: ['Quarter past', 'Quarter to', 'Half past', 'On the dot'],
                correct: 2,
                explanation: '"E mezza" means half past — added directly after the hour.',
            },
            {
                type: 'question',
                question: 'How would you say "quarter to five"?',
                options: ['Sono le cinque e un quarto', 'Sono le cinque meno un quarto', 'È le cinque meno un quarto', 'Sono le quattro e un quarto'],
                correct: 1,
                explanation: 'Sono le cinque meno un quarto — literally "it\'s five minus a quarter".',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now tell the time in Italian, including the one tricky exception with "è l\'una". Next — counting objects and quantities.',
                xp: 20,
            },
        ],
    },
    'counting-objects-and-quantities': {
        title: 'Counting objects and quantities',
        topic: 'Numbers & counting',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'How many?',
                body: 'Beyond exact numbers, you\'ll often need to describe quantities loosely — a lot, a little, all of something. These words come up constantly in daily conversation.',
                emoji: '📦',
            },
            { type: 'vocabulary', word: 'Quanti / Quante?', translation: 'How many? (masc. / fem.)', phonetic: '/ˈkwanti, ˈkwante/', note: 'Must match the gender of the noun — quanti libri (how many books, masc.), quante mele (how many apples, fem.).', emoji: '❓' },
            { type: 'vocabulary', word: 'Molto / Molti', translation: 'A lot / Many', phonetic: '/ˈmolto, ˈmolti/', note: 'Molto for uncountable things, molti for countable plural things.', emoji: '📈' },
            { type: 'vocabulary', word: 'Poco / Pochi', translation: 'A little / Few', phonetic: '/ˈpɔːko, ˈpɔːki/', note: 'The opposite of molto/molti — same gender and number rules apply.', emoji: '📉' },
            { type: 'vocabulary', word: 'Un po\' di', translation: 'A bit of / Some', phonetic: '/un pɔ di/', note: 'A very common, casual way to say "some" — un po\' di pane (a bit of bread).', emoji: '🍞' },
            { type: 'vocabulary', word: 'Tutto / Tutti', translation: 'All / Everyone', phonetic: '/ˈtutto, ˈtutti/', note: 'Tutto for "everything/all", tutti for "everyone" or "all" with plural countable nouns.', emoji: '✅' },
            {
                type: 'question',
                question: 'Which word means "many books" correctly?',
                options: ['Molto libri', 'Molti libri', 'Poco libri', 'Tutto libri'],
                correct: 1,
                explanation: 'Molti libri is correct — "molti" matches the plural masculine noun "libri".',
            },
            {
                type: 'question',
                question: 'What does "un po\' di" mean?',
                options: ['A lot of', 'None of', 'A bit of', 'All of'],
                correct: 2,
                explanation: 'Un po\' di means "a bit of" or "some" — a casual, common phrase in everyday Italian.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now describe quantities naturally in Italian. One more lesson to go — phone numbers and dates.',
                xp: 15,
            },
        ],
    },
    'phone-numbers-and-dates': {
        title: 'Phone numbers and dates',
        topic: 'Numbers & counting',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Numbers in real life',
                body: 'Phone numbers and dates use numbers in slightly different ways than counting. Italians often read phone numbers in pairs, and dates follow their own format.',
                emoji: '📱',
            },
            { type: 'vocabulary', word: 'Qual è il tuo numero?', translation: 'What\'s your number?', phonetic: '/kwal ɛ il ˈtuːo ˈnuːmero/', note: 'A natural way to ask for someone\'s phone number in a casual setting.', emoji: '📱' },
            { type: 'vocabulary', word: 'Il mio numero è...', translation: 'My number is...', phonetic: '/il ˈmiːo ˈnuːmero ɛ/', note: 'Italians often read phone numbers in pairs — "tre, quaranta, dodici" rather than digit by digit.', emoji: '📞' },
            { type: 'vocabulary', word: 'Che giorno è oggi?', translation: 'What day is it today?', phonetic: '/ke ˈdʒorno ɛ ˈɔddʒi/', note: 'Used for asking the date, not the day of the week — that\'s a separate question.', emoji: '📅' },
            { type: 'vocabulary', word: 'Oggi è il...', translation: 'Today is the...', phonetic: '/ˈɔddʒi ɛ il/', note: 'Followed by the day number and month — "Oggi è il quindici marzo" (Today is March 15th).', emoji: '📆' },
            { type: 'vocabulary', word: 'il primo', translation: 'the first (of the month)', phonetic: '/il ˈpriːmo/', note: 'The only date that uses an ordinal number — every other date uses cardinal numbers (il due, il tre...).', emoji: '1️⃣' },
            {
                type: 'question',
                question: 'How do Italians typically read phone numbers aloud?',
                options: ['Digit by digit only', 'Often in pairs', 'Always backwards', 'Never aloud'],
                correct: 1,
                explanation: 'Italians often group phone number digits in pairs when speaking them, unlike the digit-by-digit style common in English.',
            },
            {
                type: 'question',
                question: 'What\'s special about "il primo" (the first)?',
                options: ['It\'s feminine', 'It uses an ordinal number, unlike other dates', 'It\'s only used for years', 'It means "the last"'],
                correct: 1,
                explanation: 'Il primo (the first) is the only date in Italian that uses an ordinal number — every other day uses cardinal numbers like il due, il tre.',
            },
            {
                type: 'complete',
                title: 'Numbers & counting — complete!',
                body: 'You\'ve finished all 5 lessons in this topic. You can now count, tell time, describe quantities, and handle phone numbers and dates in Italian.',
                xp: 15,
            },
        ],
    },
    'talking-about-your-age': {
        title: 'Talking about your age',
        topic: 'Numbers & counting',
        duration: '5 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Putting numbers to use — your age',
                body: 'You\'ve learned the numbers — now let\'s use them for one of the most common everyday topics: talking about age.',
                emoji: '🎂',
            },
            {
                type: 'vocabulary',
                word: 'Quanti anni hai?',
                translation: 'How old are you? (informal)',
                phonetic: '/ˈkwanti ˈanni ˈai/',
                note: 'Literally "how many years do you have?" — Italian always uses "avere" (to have), never "essere" (to be), for age.',
                emoji: '❓',
            },
            {
                type: 'vocabulary',
                word: 'Ho trent\'anni',
                translation: 'I am thirty years old',
                phonetic: '/ɔ trentˈanni/',
                note: '"Trent\'anni" — note the apostrophe replacing the final vowel of "trenta" before "anni".',
                emoji: '3️⃣0️⃣',
            },
            {
                type: 'vocabulary',
                word: 'Ho quarantadue anni',
                translation: 'I am forty-two years old',
                phonetic: '/ɔ kwarantaˈdue ˈanni/',
                note: 'No apostrophe needed here — only numbers ending in vowels that clash need the contraction (like trenta, venti).',
                emoji: '4️⃣2️⃣',
            },
            {
                type: 'vocabulary',
                word: 'Sto per compiere ... anni',
                translation: 'I\'m about to turn ... years old',
                phonetic: '/sto per ˈkompjere ˈanni/',
                note: 'Useful when your birthday is coming up soon — "sto per compiere trent\'anni" (I\'m about to turn thirty).',
                emoji: '🎉',
            },
            {
                type: 'vocabulary',
                word: 'Ho appena compiuto ... anni',
                translation: 'I just turned ... years old',
                phonetic: '/ɔ apˈpena komˈpjuːto ˈanni/',
                note: 'Uses passato prossimo — for a birthday that just happened.',
                emoji: '🎈',
            },
            {
                type: 'question',
                question: 'How do you say "I am thirty years old"?',
                options: ['Sono trent\'anni', 'Ho trent\'anni', 'Ho trenta anni', 'Sono trenta anni'],
                correct: 1,
                explanation: '"Ho trent\'anni" is correct — using "avere" and the apostrophe contraction before "anni".',
            },
            {
                type: 'question',
                question: 'Why does "trenta" become "trent\'" before "anni"?',
                options: ['No reason, both are correct', 'The final vowel is dropped and replaced with an apostrophe before a word starting with a vowel', 'It\'s a completely different word', 'Only used in formal speech'],
                correct: 1,
                explanation: 'Trenta drops its final "a" before "anni" (which starts with a vowel), replaced by an apostrophe — trent\'anni.',
            },
            {
                type: 'question',
                question: 'Does "quarantadue" need an apostrophe before "anni"?',
                options: ['Yes, always', 'No — only some numbers like trenta and venti need the contraction', 'Only in formal Italian', 'Only for numbers under 20'],
                correct: 1,
                explanation: 'No apostrophe needed here — the contraction only applies to specific numbers where vowels would clash awkwardly.',
            },
            {
                type: 'question',
                question: 'What does "sto per compiere trent\'anni" mean?',
                options: ['I just turned thirty', 'I\'m about to turn thirty', 'I was thirty last year', 'I will never turn thirty'],
                correct: 1,
                explanation: '"Stare per" + infinitive means "to be about to" — "sto per compiere trent\'anni" means I\'m about to turn thirty.',
            },
            {
                type: 'question',
                question: 'What does "ho appena compiuto ventotto anni" mean?',
                options: ['I will turn twenty-eight soon', 'I just turned twenty-eight', 'I am not twenty-eight yet', 'I turned twenty-eight last year'],
                correct: 1,
                explanation: '"Appena" + passato prossimo means "just" — describing a birthday that happened very recently.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now confidently talk about your age using the numbers you\'ve learned. Next up — prices and money.',
                xp: 15,
            },
        ],
    },
    'prices-and-money': {
        title: 'Prices and money',
        topic: 'Numbers & counting',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Talking about euros and cents',
                body: 'Whether shopping, dining out or paying bills, handling money confidently is essential. Let\'s put your numbers to work with euros and cents.',
                emoji: '💶',
            },
            {
                type: 'vocabulary',
                word: 'Quanto costa?',
                translation: 'How much does it cost?',
                phonetic: '/ˈkwanto ˈkɔsta/',
                note: 'The single most useful phrase for shopping — used for one item. For multiple items, use "Quanto costano?"',
                emoji: '❓',
            },
            {
                type: 'vocabulary',
                word: 'Costa dieci euro',
                translation: 'It costs ten euros',
                phonetic: '/ˈkɔsta ˈdjɛːtʃi ˈɛuro/',
                note: '"Euro" doesn\'t change in the plural — "dieci euro", not "dieci euri".',
                emoji: '💰',
            },
            {
                type: 'vocabulary',
                word: 'Cinquanta centesimi',
                translation: 'Fifty cents',
                phonetic: '/tʃiŋˈkwanta tʃenˈtɛːzimi/',
                note: '"Centesimo/i" means cent(s) — used for amounts under one euro.',
                emoji: '🪙',
            },
            {
                type: 'vocabulary',
                word: 'È troppo caro',
                translation: 'It\'s too expensive',
                phonetic: '/ɛ ˈtroppo ˈkaːro/',
                note: 'A common, useful phrase when negotiating or expressing surprise at a price.',
                emoji: '😬',
            },
            {
                type: 'vocabulary',
                word: 'È economico',
                translation: 'It\'s cheap / affordable',
                phonetic: '/ɛ ekoˈnɔːmiko/',
                note: 'The opposite of "caro" — a positive way to describe good value.',
                emoji: '💸',
            },
            {
                type: 'vocabulary',
                word: 'Quanto viene in totale?',
                translation: 'How much does it come to in total?',
                phonetic: '/ˈkwanto ˈvjɛːne in totaːle/',
                note: 'Useful when buying multiple items and wanting the final total.',
                emoji: '🧾',
            },
            {
                type: 'question',
                question: 'How would you ask the price of one item?',
                options: ['Quanto costano?', 'Quanto costa?', 'Quanti soldi?', 'Quanto vale?'],
                correct: 1,
                explanation: '"Quanto costa?" is used for a single item. "Quanto costano?" is used when asking about multiple items.',
            },
            {
                type: 'question',
                question: 'What is the plural of "euro"?',
                options: ['Euri', 'Euro (unchanged)', 'Euros', 'Eura'],
                correct: 1,
                explanation: '"Euro" does not change in the plural — "dieci euro", not "dieci euri".',
            },
            {
                type: 'question',
                question: 'What does "è troppo caro" mean?',
                options: ['It\'s very cheap', 'It\'s too expensive', 'It\'s free', 'It\'s on sale'],
                correct: 1,
                explanation: '"È troppo caro" means it\'s too expensive — a useful phrase when negotiating prices.',
            },
            {
                type: 'question',
                question: 'What is the opposite of "caro"?',
                options: ['Costoso', 'Economico', 'Troppo', 'Totale'],
                correct: 1,
                explanation: '"Economico" (cheap/affordable) is the opposite of "caro" (expensive).',
            },
            {
                type: 'question',
                question: 'What are "cinquanta centesimi"?',
                options: ['Fifty euros', 'Fifty cents', 'Fifteen euros', 'Fifty percent'],
                correct: 1,
                explanation: '"Centesimo/i" means cent(s) — "cinquanta centesimi" means fifty cents.',
            },
            {
                type: 'question',
                question: 'How would you ask for the total price of several items?',
                options: ['Quanto costa?', 'Quanto viene in totale?', 'È economico?', 'Quanti euro hai?'],
                correct: 1,
                explanation: '"Quanto viene in totale?" asks for the final total when buying multiple items.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now confidently discuss prices, ask for totals and describe whether something is expensive or cheap. Next up — ordinal numbers.',
                xp: 15,
            },
        ],
    },
    'ordinal-numbers': {
        title: 'Ordinal numbers',
        topic: 'Numbers & counting',
        duration: '6 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'First, second, third',
                body: 'Ordinal numbers describe order and position — essential for dates, floors, rankings and more. Let\'s learn the first ten.',
                emoji: '🥇',
            },
            {
                type: 'vocabulary',
                word: 'Primo / prima',
                translation: 'First (masc./fem.)',
                phonetic: '/ˈpriːmo/',
                note: 'Ordinal numbers agree in gender and number with the noun — "il primo giorno" (the first day), "la prima volta" (the first time).',
                emoji: '1️⃣',
            },
            {
                type: 'vocabulary',
                word: 'Secondo / seconda',
                translation: 'Second',
                phonetic: '/seˈkondo/',
                note: '"Il secondo piatto" — the second course (of a meal). Very commonly used in restaurants.',
                emoji: '2️⃣',
            },
            {
                type: 'vocabulary',
                word: 'Terzo / terza',
                translation: 'Third',
                phonetic: '/ˈtɛrtso/',
                note: '"Il terzo piano" — the third floor. Ordinals are used constantly for building floors in Italy.',
                emoji: '3️⃣',
            },
            {
                type: 'vocabulary',
                word: 'Quarto / quinto',
                translation: 'Fourth / Fifth',
                phonetic: '/ˈkwarto, ˈkwinto/',
                note: 'From here on, most ordinals are formed by adding "-esimo" to the cardinal number — but 4th and 5th are irregular-looking, so learn them directly.',
                emoji: '4️⃣',
            },
            {
                type: 'vocabulary',
                word: 'Sesto / settimo',
                translation: 'Sixth / Seventh',
                phonetic: '/ˈsɛsto, ˈsɛttimo/',
                note: 'Continuing the pattern — these come up in dates, rankings and addresses.',
                emoji: '6️⃣',
            },
            {
                type: 'vocabulary',
                word: 'Ottavo / nono / decimo',
                translation: 'Eighth / Ninth / Tenth',
                phonetic: '/otˈtaːvo, ˈnɔːno, ˈdɛtʃimo/',
                note: 'The last three of the first ten — after "decimo" (10th), ordinals are formed regularly: number + "-esimo".',
                emoji: '🔟',
            },
            {
                type: 'vocabulary',
                word: 'Il primo piano',
                translation: 'The first floor',
                phonetic: '/il ˈpriːmo ˈpjaːno/',
                note: 'Important cultural note: in Italy, "il primo piano" is one floor above ground level — the ground floor is "il pianterreno".',
                emoji: '🏢',
            },
            {
                type: 'question',
                question: 'What is special about how ordinal numbers behave?',
                options: ['They never change', 'They agree in gender and number with the noun', 'They are only used for dates', 'They replace cardinal numbers entirely'],
                correct: 1,
                explanation: 'Ordinal numbers agree in gender and number — "il primo giorno" (masc.) vs "la prima volta" (fem.).',
            },
            {
                type: 'question',
                question: 'What does "il secondo piatto" refer to?',
                options: ['The second floor', 'The second course of a meal', 'The second day', 'The second person'],
                correct: 1,
                explanation: '"Il secondo piatto" means the second course — very commonly used in Italian restaurant culture.',
            },
            {
                type: 'question',
                question: 'In Italy, what is "il primo piano"?',
                options: ['The ground floor', 'The floor one level above ground', 'The top floor', 'The basement'],
                correct: 1,
                explanation: 'Important cultural note — "il primo piano" is one level above ground floor. The ground floor is "il pianterreno".',
            },
            {
                type: 'question',
                question: 'How are most ordinal numbers formed after "decimo" (10th)?',
                options: ['They are all irregular', 'Cardinal number + "-esimo"', 'They stay the same as cardinal numbers', 'They use a completely different system'],
                correct: 1,
                explanation: 'After decimo, ordinals follow a regular pattern: the cardinal number plus the ending "-esimo".',
            },
            {
                type: 'question',
                question: 'Which is the correct ordinal for "third"?',
                options: ['Trino', 'Terzo', 'Tres', 'Tricesimo'],
                correct: 1,
                explanation: '"Terzo" (or "terza" for feminine) is the correct ordinal number for third.',
            },
            {
                type: 'question',
                question: 'How would you say "the fifth floor"?',
                options: ['Il quarto piano', 'Il quinto piano', 'Il sesto piano', 'Il cinque piano'],
                correct: 1,
                explanation: '"Il quinto piano" is correct — "quinto" is the ordinal form of five, agreeing with the masculine noun "piano".',
            },
            {
                type: 'complete',
                title: 'Numbers & counting — complete!',
                body: 'You\'ve finished all 10 lessons in this topic! You can now count, tell time, handle money, describe quantities, and use ordinal numbers confidently in Italian.',
                xp: 20,
            },
        ],
    },
    'immediate-family-members': {
        title: 'Immediate family members',
        topic: 'Family & relationships',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Talking about your immediate family',
                body: 'Family is one of the first things people ask about in Italian. Let\'s learn the essential words for your closest family members.',
                emoji: '👨‍👩‍👧',
            },
            {
                type: 'vocabulary',
                word: 'La madre',
                translation: 'Mother',
                phonetic: '/la ˈmaːdre/',
                note: 'Also commonly called "mamma" in everyday speech — much warmer and more common than "madre" alone.',
                emoji: '👩',
            },
            {
                type: 'vocabulary',
                word: 'Il padre',
                translation: 'Father',
                phonetic: '/il ˈpaːdre/',
                note: 'Also commonly called "papà" — used constantly in daily conversation, more so than "padre".',
                emoji: '👨',
            },
            {
                type: 'vocabulary',
                word: 'Il fratello',
                translation: 'Brother',
                phonetic: '/il fraˈtɛllo/',
                note: 'Plural is "i fratelli" — and can also mean "siblings" collectively when gender is mixed.',
                emoji: '👦',
            },
            {
                type: 'vocabulary',
                word: 'La sorella',
                translation: 'Sister',
                phonetic: '/la soˈrɛlla/',
                note: 'Plural is "le sorelle". "Ho due sorelle" — I have two sisters.',
                emoji: '👧',
            },
            {
                type: 'vocabulary',
                word: 'I genitori',
                translation: 'Parents',
                phonetic: '/i dʒeniˈtoːri/',
                note: 'Always plural in this form. "I miei genitori" — my parents.',
                emoji: '👨‍👩‍👦',
            },
            {
                type: 'vocabulary',
                word: 'Il figlio',
                translation: 'Son',
                phonetic: '/il ˈfiʎʎo/',
                note: 'Plural "i figli" also means "children" collectively, regardless of gender.',
                emoji: '👶',
            },
            {
                type: 'vocabulary',
                word: 'La figlia',
                translation: 'Daughter',
                phonetic: '/la ˈfiʎʎa/',
                note: 'Plural is "le figlie". "Ho una figlia" — I have a daughter.',
                emoji: '👧',
            },
            {
                type: 'vocabulary',
                word: 'Il marito',
                translation: 'Husband',
                phonetic: '/il maˈriːto/',
                note: 'Used for a legally married husband — for a partner, Italians say "il compagno" or "il fidanzato".',
                emoji: '🤵',
            },
            {
                type: 'vocabulary',
                word: 'La moglie',
                translation: 'Wife',
                phonetic: '/la ˈmoʎʎe/',
                note: 'An irregular plural: "le mogli". Used specifically for a legally married wife.',
                emoji: '👰',
            },
            {
                type: 'vocabulary',
                word: 'I fratelli',
                translation: 'Siblings / Brothers',
                phonetic: '/i fraˈtɛlli/',
                note: 'When mixed gender, "fratelli" covers both brothers and sisters together — a very common usage.',
                emoji: '👫',
            },
            {
                type: 'question',
                question: 'How do you say "mother" in a warm, everyday way?',
                options: ['Madre', 'Mamma', 'Sorella', 'Moglie'],
                correct: 1,
                explanation: '"Mamma" is the warm, everyday word for mother — much more common in daily speech than "madre".',
            },
            {
                type: 'question',
                question: 'What does "i genitori" mean?',
                options: ['Grandparents', 'Parents', 'Children', 'Siblings'],
                correct: 1,
                explanation: '"I genitori" means parents — it\'s always used in the plural form.',
            },
            {
                type: 'question',
                question: 'Which word means "daughter"?',
                options: ['Il figlio', 'La figlia', 'Il fratello', 'La sorella'],
                correct: 1,
                explanation: '"La figlia" means daughter. "Il figlio" is son — note the different endings.',
            },
            {
                type: 'question',
                question: 'What is the plural of "il fratello"?',
                options: ['I fratelli', 'Le fratelle', 'I fratello', 'Le fratelli'],
                correct: 0,
                explanation: '"I fratelli" is correct — masculine plural nouns ending in -o typically change to -i.',
            },
            {
                type: 'question',
                question: 'How would you say "I have two sisters"?',
                options: ['Ho due fratelli', 'Ho due sorelle', 'Sono due sorelle', 'Ho due figlie'],
                correct: 1,
                explanation: '"Ho due sorelle" — I have two sisters. "Sorelle" is the plural of "sorella".',
            },
            {
                type: 'question',
                question: 'Which word specifically means a legally married husband?',
                options: ['Il compagno', 'Il fidanzato', 'Il marito', 'Il padre'],
                correct: 2,
                explanation: '"Il marito" specifically means a married husband — "il compagno" or "il fidanzato" are used for partners.',
            },
            {
                type: 'question',
                question: 'What does "i figli" mean when used collectively?',
                options: ['Only sons', 'Only daughters', 'Children (regardless of gender)', 'Grandchildren'],
                correct: 2,
                explanation: '"I figli" can mean children collectively, regardless of gender — a very common everyday usage.',
            },
            {
                type: 'question',
                question: 'What is the irregular plural of "la moglie"?',
                options: ['Le moglie', 'Le mogli', 'I mogli', 'Le moglio'],
                correct: 1,
                explanation: '"Le mogli" is the correct irregular plural of "la moglie" (wife).',
            },
            {
                type: 'question',
                question: 'How do you say "father" in the warm, everyday way?',
                options: ['Padre', 'Papà', 'Marito', 'Figlio'],
                correct: 1,
                explanation: '"Papà" is the everyday, affectionate word for father — used constantly in daily conversation.',
            },
            {
                type: 'question',
                question: 'What can "i fratelli" mean besides "brothers"?',
                options: ['Sisters only', 'Siblings of mixed gender', 'Parents', 'Cousins'],
                correct: 1,
                explanation: '"I fratelli" can refer to siblings of mixed gender collectively — brothers and sisters together.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You now know the core words for your immediate family. Next up — extended family members like nonni, zii and cugini.',
                xp: 15,
            },
        ],
    },
    'extended-family-members': {
        title: 'Extended family members',
        topic: 'Family & relationships',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Beyond mom, dad and siblings',
                body: 'Italian family gatherings are famous for a reason — the whole extended family shows up. Let\'s learn how to talk about grandparents, aunts, uncles and cousins.',
                emoji: '👨‍👩‍👧‍👦',
            },
            {
                type: 'vocabulary',
                word: 'Il nonno',
                translation: 'Grandfather',
                phonetic: '/il ˈnɔnno/',
                note: 'Plural is "i nonni", which also means "grandparents" collectively.',
                emoji: '👴',
            },
            {
                type: 'vocabulary',
                word: 'La nonna',
                translation: 'Grandmother',
                phonetic: '/la ˈnɔnna/',
                note: 'Plural is "le nonne". Often a central figure in Italian family life.',
                emoji: '👵',
            },
            {
                type: 'vocabulary',
                word: 'Lo zio',
                translation: 'Uncle',
                phonetic: '/lo ˈtsiːo/',
                note: 'Uses "lo" instead of "il" because it starts with "z" — an important article rule.',
                emoji: '👨',
            },
            {
                type: 'vocabulary',
                word: 'La zia',
                translation: 'Aunt',
                phonetic: '/la ˈtsiːa/',
                note: 'Plural is "le zie". "Mia zia" — my aunt.',
                emoji: '👩',
            },
            {
                type: 'vocabulary',
                word: 'Il cugino',
                translation: 'Cousin (male)',
                phonetic: '/il kuˈdʒiːno/',
                note: 'Plural "i cugini" also covers mixed-gender cousins collectively.',
                emoji: '🧑',
            },
            {
                type: 'vocabulary',
                word: 'La cugina',
                translation: 'Cousin (female)',
                phonetic: '/la kuˈdʒiːna/',
                note: 'Plural is "le cugine" when referring only to female cousins.',
                emoji: '👩',
            },
            {
                type: 'vocabulary',
                word: 'Il nipote',
                translation: 'Nephew / Grandson',
                phonetic: '/il niˈpoːte/',
                note: 'This single word covers both meanings — context makes it clear which is meant.',
                emoji: '👦',
            },
            {
                type: 'vocabulary',
                word: 'La nipote',
                translation: 'Niece / Granddaughter',
                phonetic: '/la niˈpoːte/',
                note: 'Same word as nephew/grandson but feminine — only the article changes.',
                emoji: '👧',
            },
            {
                type: 'vocabulary',
                word: 'Il suocero',
                translation: 'Father-in-law',
                phonetic: '/il ˈswɔtʃero/',
                note: 'Along with "la suocera" (mother-in-law), these come up often once you\'re married.',
                emoji: '🧓',
            },
            {
                type: 'vocabulary',
                word: 'I parenti',
                translation: 'Relatives',
                phonetic: '/i paˈrɛnti/',
                note: 'A false friend alert — this does NOT mean "parents"! That\'s "i genitori". "I parenti" means relatives in general.',
                emoji: '👪',
            },
            {
                type: 'question',
                question: 'Why does "uncle" use "lo zio" instead of "il zio"?',
                options: ['No reason, both are correct', 'Because it starts with the letter "z"', 'Because it\'s a family word', 'Because it\'s plural'],
                correct: 1,
                explanation: 'Italian nouns starting with "z" take "lo" instead of "il" — this is a general article rule, not specific to family words.',
            },
            {
                type: 'question',
                question: 'What is a common false friend to watch out for?',
                options: ['"I parenti" means parents', '"I parenti" means relatives, not parents', '"I nonni" means uncles', '"I cugini" means grandparents'],
                correct: 1,
                explanation: '"I parenti" means relatives in general — it does NOT mean parents. "Parents" is "i genitori".',
            },
            {
                type: 'question',
                question: 'What does "il nipote" mean?',
                options: ['Only nephew', 'Only grandson', 'Nephew or grandson depending on context', 'Cousin'],
                correct: 2,
                explanation: '"Il nipote" covers both nephew and grandson — the same word is used for both meanings.',
            },
            {
                type: 'question',
                question: 'How do you say "grandmother"?',
                options: ['Il nonno', 'La nonna', 'La zia', 'La suocera'],
                correct: 1,
                explanation: '"La nonna" means grandmother. "Il nonno" is grandfather.',
            },
            {
                type: 'question',
                question: 'What does "i nonni" mean when used collectively?',
                options: ['Uncles and aunts', 'Grandparents', 'Cousins', 'In-laws'],
                correct: 1,
                explanation: '"I nonni" collectively means grandparents — combining "il nonno" and "la nonna".',
            },
            {
                type: 'question',
                question: 'Which word means "father-in-law"?',
                options: ['Il suocero', 'Lo zio', 'Il nipote', 'Il cugino'],
                correct: 0,
                explanation: '"Il suocero" means father-in-law. "La suocera" is mother-in-law.',
            },
            {
                type: 'question',
                question: 'How would you say "my aunt" in Italian?',
                options: ['Mio zio', 'Mia zia', 'Mia nonna', 'Mia cugina'],
                correct: 1,
                explanation: '"Mia zia" means my aunt — "zia" is feminine, so it takes the feminine possessive "mia".',
            },
            {
                type: 'question',
                question: 'What is the plural of "il cugino" when referring to both male and female cousins?',
                options: ['Le cugine', 'I cugini', 'I cugino', 'Le cugini'],
                correct: 1,
                explanation: '"I cugini" is used both for male cousins specifically and for cousins of mixed gender collectively.',
            },
            {
                type: 'question',
                question: 'Which article is used with "zio"?',
                options: ['Il', 'Lo', 'La', 'L\''],
                correct: 1,
                explanation: '"Lo zio" — "lo" is used before masculine nouns starting with "z", "s+consonant", "gn", "ps" and a few others.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You now know how to talk about your whole extended family in Italian. Next up — describing your family as a whole.',
                xp: 15,
            },
        ],
    },
    'describing-your-family': {
        title: 'Describing your family',
        topic: 'Family & relationships',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Talking about your family as a whole',
                body: 'Now that you know the family words, let\'s put them together into full sentences describing your family — its size, its closeness, and where everyone lives.',
                emoji: '💬',
            },
            {
                type: 'vocabulary',
                word: 'Siamo in quattro',
                translation: 'There are four of us',
                phonetic: '/ˈsjaːmo in ˈkwattro/',
                note: 'A natural way to describe family size — literally "we are in four".',
                emoji: '👪',
            },
            {
                type: 'vocabulary',
                word: 'Una famiglia numerosa',
                translation: 'A large family',
                phonetic: '/ˈuna faˈmiʎʎa numeˈroːza/',
                note: 'Used for families with many members — very common in traditional Italian households.',
                emoji: '👨‍👩‍👧‍👦',
            },
            {
                type: 'vocabulary',
                word: 'Figlio unico / Figlia unica',
                translation: 'Only child (male/female)',
                phonetic: '/ˈfiʎʎo ˈuːniko/',
                note: 'Used to describe someone with no siblings. "Sono figlio unico" — I am an only child.',
                emoji: '🧒',
            },
            {
                type: 'vocabulary',
                word: 'Andiamo molto d\'accordo',
                translation: 'We get along very well',
                phonetic: '/anˈdjaːmo ˈmolto dakˈkordo/',
                note: 'A key phrase for describing family closeness. "Andare d\'accordo" — to get along.',
                emoji: '🤝',
            },
            {
                type: 'vocabulary',
                word: 'Siamo molto uniti',
                translation: 'We are very close (united)',
                phonetic: '/ˈsjaːmo ˈmolto uˈniːti/',
                note: 'Describes emotional closeness within a family — a common, warm expression.',
                emoji: '❤️',
            },
            {
                type: 'vocabulary',
                word: 'Vivono lontano',
                translation: 'They live far away',
                phonetic: '/ˈviːvono lonˈtaːno/',
                note: 'Useful when family members live in different cities or countries.',
                emoji: '📍',
            },
            {
                type: 'vocabulary',
                word: 'Vivono vicino',
                translation: 'They live nearby',
                phonetic: '/ˈviːvono viˈtʃiːno/',
                note: 'The opposite of "lontano" — describes family members living close by.',
                emoji: '🏘️',
            },
            {
                type: 'vocabulary',
                word: 'La mia famiglia è...',
                translation: 'My family is...',
                phonetic: '/la ˈmiːa faˈmiʎʎa ɛ/',
                note: 'A flexible sentence starter — follow with any adjective like "grande", "piccola", "unita".',
                emoji: '👨‍👩‍👧',
            },
            {
                type: 'vocabulary',
                word: 'Ci vediamo spesso',
                translation: 'We see each other often',
                phonetic: '/tʃi veˈdjaːmo ˈspesso/',
                note: 'Describes frequency of contact with family — a natural, common phrase.',
                emoji: '🔄',
            },
            {
                type: 'vocabulary',
                word: 'Ci sentiamo ogni giorno',
                translation: 'We talk to each other every day',
                phonetic: '/tʃi senˈtjaːmo ˈɔɲɲi ˈdʒorno/',
                note: '"Sentirsi" here means to be in touch by phone or message, not physically hearing.',
                emoji: '📞',
            },
            {
                type: 'question',
                question: 'How would you say "There are four of us"?',
                options: ['Siamo quattro persone', 'Siamo in quattro', 'Abbiamo quattro', 'Ho quattro famiglia'],
                correct: 1,
                explanation: '"Siamo in quattro" is the natural way to describe family or group size in Italian.',
            },
            {
                type: 'question',
                question: 'What does "figlio unico" mean?',
                options: ['Only child (male)', 'Only child (female)', 'First child', 'Youngest child'],
                correct: 0,
                explanation: '"Figlio unico" means only child, masculine form. "Figlia unica" is the feminine form.',
            },
            {
                type: 'question',
                question: 'What does "andiamo molto d\'accordo" mean?',
                options: ['We argue a lot', 'We get along very well', 'We live together', 'We rarely see each other'],
                correct: 1,
                explanation: '"Andare d\'accordo" means to get along — a key phrase for describing family relationships.',
            },
            {
                type: 'question',
                question: 'What is the opposite of "vivono lontano"?',
                options: ['Vivono insieme', 'Vivono vicino', 'Vivono qui', 'Vivono sempre'],
                correct: 1,
                explanation: '"Vivono vicino" (they live nearby) is the opposite of "vivono lontano" (they live far away).',
            },
            {
                type: 'question',
                question: 'What does "siamo molto uniti" describe?',
                options: ['Physical distance', 'Emotional closeness', 'Family size', 'Family arguments'],
                correct: 1,
                explanation: '"Siamo molto uniti" describes emotional closeness — being a close, united family.',
            },
            {
                type: 'question',
                question: 'What does "ci sentiamo ogni giorno" mean?',
                options: ['We feel the same every day', 'We talk to each other every day', 'We meet every day', 'We disagree every day'],
                correct: 1,
                explanation: '"Sentirsi" in this context means staying in touch by phone or message — we talk every day.',
            },
            {
                type: 'question',
                question: 'How would you describe a large family?',
                options: ['Una famiglia piccola', 'Una famiglia numerosa', 'Una famiglia unica', 'Una famiglia lontana'],
                correct: 1,
                explanation: '"Una famiglia numerosa" describes a large family with many members.',
            },
            {
                type: 'question',
                question: 'What does "ci vediamo spesso" mean?',
                options: ['We rarely see each other', 'We see each other often', 'We never see each other', 'We saw each other once'],
                correct: 1,
                explanation: '"Ci vediamo spesso" means we see each other often — describing frequency of contact.',
            },
            {
                type: 'question',
                question: 'Complete: "La mia famiglia è molto..."',
                options: ['unita', 'unico', 'uniti', 'unica'],
                correct: 0,
                explanation: '"Unita" agrees with the feminine singular noun "famiglia" — "la mia famiglia è molto unita".',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now describe your family\'s size, closeness and dynamics in Italian. Next up — talking specifically about your parents.',
                xp: 15,
            },
        ],
    },
    'talking-about-your-parents': {
        title: 'Talking about your parents',
        topic: 'Family & relationships',
        duration: '7 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'Mio, mia, i miei — talking about "my"',
                body: 'This lesson focuses on possessive adjectives with family members — a tricky but essential grammar point, since family words behave differently from other nouns.',
                emoji: '👨‍👩',
            },
            {
                type: 'vocabulary',
                word: 'Mio padre',
                translation: 'My father',
                phonetic: '/ˈmiːo ˈpaːdre/',
                note: 'Singular family members normally drop the article before the possessive — "mio padre", not "il mio padre".',
                emoji: '👨',
            },
            {
                type: 'vocabulary',
                word: 'Mia madre',
                translation: 'My mother',
                phonetic: '/ˈmiːa ˈmaːdre/',
                note: 'Same rule — no article needed. "Mia madre lavora" — My mother works.',
                emoji: '👩',
            },
            {
                type: 'vocabulary',
                word: 'I miei genitori',
                translation: 'My parents',
                phonetic: '/i ˈmjɛi dʒeniˈtoːri/',
                note: 'Unlike singular family words, plural ones DO keep the article — "i miei genitori", not "miei genitori".',
                emoji: '👨‍👩‍👦',
            },
            {
                type: 'vocabulary',
                word: 'Tuo padre',
                translation: 'Your father',
                phonetic: '/ˈtuːo ˈpaːdre/',
                note: 'The informal "your" — same article-dropping rule applies to singular family members.',
                emoji: '👨',
            },
            {
                type: 'vocabulary',
                word: 'Mio padre lavora come...',
                translation: 'My father works as a...',
                phonetic: '/ˈmiːo ˈpaːdre laˈvoːra ˈkome/',
                note: 'A useful sentence starter for talking about your parents\' jobs.',
                emoji: '💼',
            },
            {
                type: 'vocabulary',
                word: 'Mia madre è casalinga',
                translation: 'My mother is a housewife',
                phonetic: '/ˈmiːa ˈmaːdre ɛ kazaˈliŋɡa/',
                note: 'One common way to describe a parent\'s role at home.',
                emoji: '🏠',
            },
            {
                type: 'vocabulary',
                word: 'I miei sono divorziati',
                translation: 'My parents are divorced',
                phonetic: '/i ˈmjɛi ˈsono divorˈtsjaːti/',
                note: '"I miei" alone (without "genitori") is a very common casual way to say "my parents".',
                emoji: '💔',
            },
            {
                type: 'vocabulary',
                word: 'Assomiglio a mio padre',
                translation: 'I look like my father',
                phonetic: '/assoˈmiʎʎo a ˈmiːo ˈpaːdre/',
                note: '"Assomigliare a" — to resemble. Very commonly used when comparing appearances within families.',
                emoji: '👨‍🦱',
            },
            {
                type: 'vocabulary',
                word: 'Sono molto legato/a a mia madre',
                translation: 'I am very close to my mother',
                phonetic: '/ˈsono ˈmolto leˈɡaːto a ˈmiːa ˈmaːdre/',
                note: '"Legato" (masc.) / "legata" (fem.) a — literally "tied to" — a warm way to express closeness.',
                emoji: '❤️',
            },
            {
                type: 'vocabulary',
                word: 'Mio padre mi ha insegnato...',
                translation: 'My father taught me...',
                phonetic: '/ˈmiːo ˈpaːdre mi a insenˈɲaːto/',
                note: 'A useful phrase for talking about lessons or values passed down from a parent.',
                emoji: '📖',
            },
            {
                type: 'question',
                question: 'Which is correct for "my father"?',
                options: ['Il mio padre', 'Mio padre', 'Il padre mio', 'Mia padre'],
                correct: 1,
                explanation: '"Mio padre" is correct — singular family members drop the article before the possessive adjective.',
            },
            {
                type: 'question',
                question: 'Which is correct for "my parents"?',
                options: ['Miei genitori', 'I miei genitori', 'Il miei genitori', 'I mio genitori'],
                correct: 1,
                explanation: '"I miei genitori" keeps the article, unlike singular family words — plural family nouns follow the normal rule.',
            },
            {
                type: 'question',
                question: 'What does "i miei" mean when used alone?',
                options: ['My things', 'My parents', 'My friends', 'My siblings'],
                correct: 1,
                explanation: '"I miei" (without "genitori") is a common casual way of saying "my parents" in Italian.',
            },
            {
                type: 'question',
                question: 'What does "assomiglio a mio padre" mean?',
                options: ['I disagree with my father', 'I look like my father', 'I live with my father', 'I call my father'],
                correct: 1,
                explanation: '"Assomigliare a" means to resemble — "assomiglio a mio padre" means I look like my father.',
            },
            {
                type: 'question',
                question: 'Why does "mio padre" NOT use an article, but "i miei genitori" does?',
                options: ['No real reason, both are optional', 'Singular family nouns drop the article; plural ones keep it', 'It depends on formality', 'Genitori is not a family word'],
                correct: 1,
                explanation: 'This is a genuine Italian grammar rule — singular unmodified family nouns (madre, padre, fratello, sorella etc.) drop the article with possessives, but plurals keep it.',
            },
            {
                type: 'question',
                question: 'How would you say "my mother is a housewife"?',
                options: ['Mia madre è casalinga', 'La mia madre è casalinga', 'Mio madre è casalinga', 'Mia madre casalinga'],
                correct: 0,
                explanation: '"Mia madre è casalinga" is correct — no article before "madre", and "è" (is) is needed to complete the sentence.',
            },
            {
                type: 'question',
                question: 'What does "sono molto legato a mia madre" express?',
                options: ['Distance from mother', 'Closeness to mother', 'Disagreement with mother', 'Living with mother'],
                correct: 1,
                explanation: '"Legato/a a" literally means "tied to" and expresses emotional closeness to someone.',
            },
            {
                type: 'question',
                question: 'Complete: "___ padre lavora come medico." (Your father works as a doctor, informal)',
                options: ['Il tuo', 'Tuo', 'Il suo', 'Suo'],
                correct: 1,
                explanation: '"Tuo padre" is correct — the article drops before singular family nouns, same rule as "mio padre".',
            },
            {
                type: 'question',
                question: 'What does "i miei sono divorziati" mean?',
                options: ['My parents are married', 'My parents are divorced', 'My parents are traveling', 'My parents are working'],
                correct: 1,
                explanation: '"I miei sono divorziati" means my parents are divorced — "i miei" here means "my parents".',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You now understand how possessives work with family words, including the tricky article rule. Next up — talking about siblings.',
                xp: 20,
            },
        ],
    },
    'talking-about-siblings': {
        title: 'Talking about siblings',
        topic: 'Family & relationships',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Brothers, sisters and birth order',
                body: 'Beyond just naming siblings, Italians often talk about birth order and closeness. Let\'s learn the natural phrases for describing your brothers and sisters.',
                emoji: '👫',
            },
            {
                type: 'vocabulary',
                word: 'Ho un fratello e una sorella',
                translation: 'I have a brother and a sister',
                phonetic: '/ɔ un fraˈtɛllo e ˈuna soˈrɛlla/',
                note: 'A natural full sentence — "ho" (I have) + the siblings you have.',
                emoji: '👫',
            },
            {
                type: 'vocabulary',
                word: 'Sono figlio/a unico/a',
                translation: 'I am an only child',
                phonetic: '/ˈsono ˈfiʎʎo ˈuːniko/',
                note: 'Review from earlier — "figlio unico" (male) or "figlia unica" (female).',
                emoji: '🧒',
            },
            {
                type: 'vocabulary',
                word: 'Il fratello maggiore',
                translation: 'The older brother',
                phonetic: '/il fraˈtɛllo madˈdʒoːre/',
                note: '"Maggiore" means older/elder. Also used for "la sorella maggiore" (older sister).',
                emoji: '🧑',
            },
            {
                type: 'vocabulary',
                word: 'Il fratello minore',
                translation: 'The younger brother',
                phonetic: '/il fraˈtɛllo miˈnoːre/',
                note: '"Minore" means younger. Same word works for "la sorella minore" (younger sister).',
                emoji: '🧒',
            },
            {
                type: 'vocabulary',
                word: 'Sono il primogenito / la primogenita',
                translation: 'I am the firstborn',
                phonetic: '/ˈsono il primoˈdʒɛnito/',
                note: 'A specific term for the eldest child — very commonly used in family discussions.',
                emoji: '1️⃣',
            },
            {
                type: 'vocabulary',
                word: 'Sono l\'ultimo / l\'ultima',
                translation: 'I am the youngest (the last)',
                phonetic: '/ˈsono ˈultimo/',
                note: 'Literally "I am the last" — a natural way to say you\'re the youngest sibling.',
                emoji: '🔚',
            },
            {
                type: 'vocabulary',
                word: 'Litighiamo spesso',
                translation: 'We argue often',
                phonetic: '/litiˈɡjaːmo ˈspesso/',
                note: 'From "litigare" (to argue) — a realistic, common phrase between siblings.',
                emoji: '😤',
            },
            {
                type: 'vocabulary',
                word: 'Andiamo molto d\'accordo',
                translation: 'We get along very well',
                phonetic: '/anˈdjaːmo ˈmolto dakˈkordo/',
                note: 'Same useful phrase from an earlier lesson, applied here to siblings specifically.',
                emoji: '🤝',
            },
            {
                type: 'vocabulary',
                word: 'Ho due fratelli più grandi',
                translation: 'I have two older brothers',
                phonetic: '/ɔ ˈduːe fraˈtɛlli pjuː ˈɡrandi/',
                note: '"Più grande/i" (bigger/older) is a common alternative to "maggiore" for describing age.',
                emoji: '👦👦',
            },
            {
                type: 'vocabulary',
                word: 'Siamo gemelli',
                translation: 'We are twins',
                phonetic: '/ˈsjaːmo dʒeˈmɛlli/',
                note: '"Gemelli" (masc./mixed) or "gemelle" (fem.) — used for twin siblings.',
                emoji: '👯',
            },
            {
                type: 'question',
                question: 'What does "il fratello maggiore" mean?',
                options: ['The younger brother', 'The older brother', 'The only brother', 'The twin brother'],
                correct: 1,
                explanation: '"Maggiore" means older/elder — "il fratello maggiore" is the older brother.',
            },
            {
                type: 'question',
                question: 'What does "sono il primogenito" mean?',
                options: ['I am the youngest', 'I am the firstborn', 'I am an only child', 'I am a twin'],
                correct: 1,
                explanation: '"Il primogenito" (or "la primogenita" for females) specifically means the firstborn child.',
            },
            {
                type: 'question',
                question: 'How would you say "I am the youngest"?',
                options: ['Sono il maggiore', 'Sono il primogenito', 'Sono l\'ultimo', 'Sono figlio unico'],
                correct: 2,
                explanation: '"Sono l\'ultimo" (or "l\'ultima") literally means "I am the last" — the natural way to say you\'re the youngest.',
            },
            {
                type: 'question',
                question: 'What does "litighiamo spesso" mean?',
                options: ['We laugh often', 'We argue often', 'We travel often', 'We call often'],
                correct: 1,
                explanation: '"Litigare" means to argue — "litighiamo spesso" means we argue often.',
            },
            {
                type: 'question',
                question: 'What does "siamo gemelli" mean?',
                options: ['We are cousins', 'We are twins', 'We are close', 'We are far apart in age'],
                correct: 1,
                explanation: '"Gemelli" means twins (masculine or mixed group). "Gemelle" would be used for two female twins.',
            },
            {
                type: 'question',
                question: 'What is the opposite of "maggiore" when describing siblings?',
                options: ['Primogenito', 'Minore', 'Unico', 'Gemello'],
                correct: 1,
                explanation: '"Minore" (younger) is the opposite of "maggiore" (older) when describing sibling age.',
            },
            {
                type: 'question',
                question: 'How would you say "I have two older brothers"?',
                options: ['Ho due fratelli minori', 'Ho due fratelli più grandi', 'Sono il primogenito', 'Ho due sorelle maggiori'],
                correct: 1,
                explanation: '"Ho due fratelli più grandi" means I have two older brothers — "più grande/i" is a common alternative to "maggiore".',
            },
            {
                type: 'question',
                question: 'Which phrase describes a good sibling relationship?',
                options: ['Litighiamo spesso', 'Andiamo molto d\'accordo', 'Viviamo lontano', 'Siamo gemelli'],
                correct: 1,
                explanation: '"Andiamo molto d\'accordo" (we get along very well) describes a positive relationship, unlike "litighiamo spesso" (we argue often).',
            },
            {
                type: 'question',
                question: 'What\'s the feminine form of "figlio unico"?',
                options: ['Figlia unico', 'Figlia unica', 'Figlio unica', 'Figlie uniche'],
                correct: 1,
                explanation: '"Figlia unica" is the correct feminine form — both the noun and the adjective change to match gender.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now describe your siblings, birth order and sibling relationships in Italian. Next up — talking about marital status.',
                xp: 15,
            },
        ],
    },
    'marital-status': {
        title: 'Marital status',
        topic: 'Family & relationships',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Single, married, and everything in between',
                body: 'Talking about relationship status comes up naturally in conversation. Let\'s learn the key words and phrases Italians use.',
                emoji: '💍',
            },
            {
                type: 'vocabulary',
                word: 'Sono single',
                translation: 'I am single',
                phonetic: '/ˈsono ˈsiŋɡol/',
                note: 'A borrowed English word, very commonly used in everyday Italian.',
                emoji: '🙋',
            },
            {
                type: 'vocabulary',
                word: 'Sono sposato / sposata',
                translation: 'I am married (male/female)',
                phonetic: '/ˈsono spoˈzaːto/',
                note: 'From "sposare" (to marry). Remember to match the ending to your own gender.',
                emoji: '💍',
            },
            {
                type: 'vocabulary',
                word: 'Sono fidanzato / fidanzata',
                translation: 'I am engaged / I have a partner',
                phonetic: '/ˈsono fidanˈtsaːto/',
                note: 'Can mean either formally engaged or simply "in a serious relationship" depending on context.',
                emoji: '💑',
            },
            {
                type: 'vocabulary',
                word: 'Il mio ragazzo',
                translation: 'My boyfriend',
                phonetic: '/il ˈmiːo raˈɡattso/',
                note: 'Literally "my boy" — the standard, casual way to say boyfriend.',
                emoji: '👦',
            },
            {
                type: 'vocabulary',
                word: 'La mia ragazza',
                translation: 'My girlfriend',
                phonetic: '/la ˈmiːa raˈɡattsa/',
                note: 'Literally "my girl" — same pattern as "ragazzo", feminine form.',
                emoji: '👧',
            },
            {
                type: 'vocabulary',
                word: 'Sono divorziato / divorziata',
                translation: 'I am divorced (male/female)',
                phonetic: '/ˈsono divorˈtsjaːto/',
                note: 'From "divorziare" (to divorce). Remember the gender agreement.',
                emoji: '💔',
            },
            {
                type: 'vocabulary',
                word: 'Sono vedovo / vedova',
                translation: 'I am a widower / widow',
                phonetic: '/ˈsono ˈveːdovo/',
                note: 'A sensitive but important word to know for describing family situations.',
                emoji: '🖤',
            },
            {
                type: 'vocabulary',
                word: 'Ci siamo sposati l\'anno scorso',
                translation: 'We got married last year',
                phonetic: '/tʃi ˈsjaːmo spoˈzaːti ˈlanno ˈskorso/',
                note: 'A natural way to talk about when a marriage happened, using passato prossimo.',
                emoji: '💒',
            },
            {
                type: 'vocabulary',
                word: 'Stiamo insieme da...',
                translation: 'We\'ve been together for...',
                phonetic: '/ˈstjaːmo inˈsjɛme da/',
                note: 'Follow with a length of time — "stiamo insieme da tre anni" (we\'ve been together for three years).',
                emoji: '❤️',
            },
            {
                type: 'vocabulary',
                word: 'Qual è il tuo stato civile?',
                translation: 'What is your marital status?',
                phonetic: '/kwal ɛ il ˈtuːo ˈstaːto tʃiˈviːle/',
                note: 'A more formal, official phrase — used on forms or in formal conversation.',
                emoji: '📋',
            },
            {
                type: 'question',
                question: 'How would a woman say "I am married"?',
                options: ['Sono sposato', 'Sono sposata', 'Sono fidanzata', 'Sono single'],
                correct: 1,
                explanation: '"Sposata" is the feminine form — women use the "-a" ending to match their own gender.',
            },
            {
                type: 'question',
                question: 'What does "sono fidanzato" mean?',
                options: ['I am married', 'I am divorced', 'I am engaged / have a partner', 'I am single'],
                correct: 2,
                explanation: '"Sono fidanzato" can mean formally engaged or simply being in a serious relationship, depending on context.',
            },
            {
                type: 'question',
                question: 'How do you say "my girlfriend"?',
                options: ['Il mio ragazzo', 'La mia ragazza', 'La mia fidanzata', 'Il mio fidanzato'],
                correct: 1,
                explanation: '"La mia ragazza" literally means "my girl" — the standard, casual way to say girlfriend.',
            },
            {
                type: 'question',
                question: 'What does "stiamo insieme da tre anni" mean?',
                options: ['We got married three years ago', 'We\'ve been together for three years', 'We met three years ago', 'We divorced three years ago'],
                correct: 1,
                explanation: '"Stiamo insieme da tre anni" means we\'ve been together for three years — describing an ongoing relationship duration.',
            },
            {
                type: 'question',
                question: 'What does "sono vedovo" mean?',
                options: ['I am single', 'I am a widower', 'I am divorced', 'I am engaged'],
                correct: 1,
                explanation: '"Sono vedovo" means I am a widower. "Vedova" is the feminine form (widow).',
            },
            {
                type: 'question',
                question: 'Which word for relationship status is borrowed directly from English?',
                options: ['Sposato', 'Fidanzato', 'Single', 'Divorziato'],
                correct: 2,
                explanation: '"Single" is a borrowed English word, used very commonly in everyday Italian.',
            },
            {
                type: 'question',
                question: 'How would you ask "What is your marital status?" formally?',
                options: ['Sei single?', 'Qual è il tuo stato civile?', 'Ti piace?', 'Come ti chiami?'],
                correct: 1,
                explanation: '"Qual è il tuo stato civile?" is the formal way to ask about marital status, often used on official forms.',
            },
            {
                type: 'question',
                question: 'How would you say "We got married last year"?',
                options: ['Stiamo insieme da un anno', 'Ci siamo sposati l\'anno scorso', 'Siamo fidanzati da un anno', 'Sono sposato da un anno'],
                correct: 1,
                explanation: '"Ci siamo sposati l\'anno scorso" uses passato prossimo to describe a completed event — getting married last year.',
            },
            {
                type: 'question',
                question: 'What word describes someone who has divorced?',
                options: ['Vedovo', 'Divorziato', 'Fidanzato', 'Single'],
                correct: 1,
                explanation: '"Divorziato" (or "divorziata") describes someone who is divorced, from the verb "divorziare".',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now describe your relationship status naturally in Italian. Next up — talking about having children.',
                xp: 15,
            },
        ],
    },
    'talking-about-children': {
        title: 'Talking about children',
        topic: 'Family & relationships',
        duration: '6 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Kids, ages and family size',
                body: 'Whether you have children or want to talk about someone else\'s, this lesson covers the essential phrases for discussing kids in Italian.',
                emoji: '👶',
            },
            {
                type: 'vocabulary',
                word: 'Ho due figli',
                translation: 'I have two children',
                phonetic: '/ɔ ˈduːe ˈfiʎʎi/',
                note: '"Figli" (plural) covers children of mixed gender collectively — even one boy and one girl.',
                emoji: '👧👦',
            },
            {
                type: 'vocabulary',
                word: 'Non ho figli',
                translation: 'I don\'t have children',
                phonetic: '/non ɔ ˈfiʎʎi/',
                note: 'Simple negative construction — "non ho" + noun.',
                emoji: '🚫',
            },
            {
                type: 'vocabulary',
                word: 'Quanti anni ha tuo figlio?',
                translation: 'How old is your son?',
                phonetic: '/ˈkwanti ˈanni a ˈtuːo ˈfiʎʎo/',
                note: 'A very natural question when someone mentions their children.',
                emoji: '❓',
            },
            {
                type: 'vocabulary',
                word: 'Ha cinque anni',
                translation: 'He/she is five years old',
                phonetic: '/a ˈtʃiŋkwe ˈanni/',
                note: 'Age in Italian always uses "avere" (to have), not "essere" (to be) — "ha cinque anni", literally "has five years".',
                emoji: '5️⃣',
            },
            {
                type: 'vocabulary',
                word: 'Sono incinta',
                translation: 'I am pregnant',
                phonetic: '/ˈsono inˈtʃinta/',
                note: 'Only used in feminine form — there is no masculine equivalent for this phrase.',
                emoji: '🤰',
            },
            {
                type: 'vocabulary',
                word: 'Aspettiamo un bambino',
                translation: 'We are expecting a baby',
                phonetic: '/aspetˈtjaːmo un bamˈbiːno/',
                note: 'A common alternative to "sono incinta" that can be said by either partner.',
                emoji: '👶',
            },
            {
                type: 'vocabulary',
                word: 'Il bambino / la bambina',
                translation: 'The (male/female) child',
                phonetic: '/il bamˈbiːno/',
                note: 'General word for young child, regardless of family relation — different from "figlio/a" which specifically means "my child".',
                emoji: '🧒',
            },
            {
                type: 'vocabulary',
                word: 'Crescere',
                translation: 'To grow / to raise (children)',
                phonetic: '/ˈkreʃʃere/',
                note: '"I bambini crescono in fretta" — children grow up fast. Also used for "raising" children.',
                emoji: '📈',
            },
            {
                type: 'vocabulary',
                word: 'Assomiglia a sua madre',
                translation: 'He/she looks like his/her mother',
                phonetic: '/assoˈmiʎʎa a ˈsua ˈmaːdre/',
                note: 'A very common comment when meeting someone\'s children.',
                emoji: '👩‍👧',
            },
            {
                type: 'vocabulary',
                word: 'Va all\'asilo / alle elementari',
                translation: 'He/she goes to preschool / elementary school',
                phonetic: '/va allaˈziːlo/',
                note: 'Useful for describing a child\'s current stage — "asilo" (nursery/preschool), "elementari" (elementary school).',
                emoji: '🏫',
            },
            {
                type: 'question',
                question: 'How do you say "I have two children"?',
                options: ['Ho due figli', 'Sono due figli', 'Ho due bambini figli', 'Ho due figlio'],
                correct: 0,
                explanation: '"Ho due figli" is correct — "figli" covers children of any gender combination.',
            },
            {
                type: 'question',
                question: 'How is age expressed in Italian?',
                options: ['With "essere" (to be)', 'With "avere" (to have)', 'With "fare" (to do)', 'With "stare" (to stay)'],
                correct: 1,
                explanation: 'Italian uses "avere" for age — "ha cinque anni" literally means "has five years", not "is five years".',
            },
            {
                type: 'question',
                question: 'What does "sono incinta" mean?',
                options: ['I am tired', 'I am pregnant', 'I am married', 'I am expecting guests'],
                correct: 1,
                explanation: '"Sono incinta" means I am pregnant — this phrase is only used in the feminine form.',
            },
            {
                type: 'question',
                question: 'What is the difference between "il bambino" and "il figlio"?',
                options: ['No difference', '"Bambino" is a general word for child; "figlio" specifically means "my/your child" (relational)', '"Figlio" means baby, "bambino" means teenager', '"Bambino" is formal, "figlio" is casual'],
                correct: 1,
                explanation: '"Il bambino" is a general term for a young child, while "il figlio" specifically expresses the family relationship (son/child of someone).',
            },
            {
                type: 'question',
                question: 'What does "aspettiamo un bambino" mean?',
                options: ['We are babysitting', 'We are expecting a baby', 'We are visiting a child', 'We are naming a baby'],
                correct: 1,
                explanation: '"Aspettiamo un bambino" means we are expecting a baby — a phrase either partner can use.',
            },
            {
                type: 'question',
                question: 'How would you ask "How old is your son?"',
                options: ['Quanti anni ha tuo figlio?', 'Quanti anni sei tuo figlio?', 'Come si chiama tuo figlio?', 'Dove vive tuo figlio?'],
                correct: 0,
                explanation: '"Quanti anni ha tuo figlio?" is correct — using "ha" (has) to ask about age, as Italian always does.',
            },
            {
                type: 'question',
                question: 'What does "assomiglia a sua madre" mean?',
                options: ['He/she lives with his/her mother', 'He/she looks like his/her mother', 'He/she calls his/her mother', 'He/she loves his/her mother'],
                correct: 1,
                explanation: '"Assomigliare a" means to resemble — a common comment when meeting someone\'s children.',
            },
            {
                type: 'question',
                question: 'What does "va all\'asilo" mean?',
                options: ['Goes to university', 'Goes to preschool/nursery', 'Goes to work', 'Goes on vacation'],
                correct: 1,
                explanation: '"Asilo" refers to nursery or preschool — for very young children before elementary school.',
            },
            {
                type: 'question',
                question: 'How would you say "I don\'t have children"?',
                options: ['Non ho figli', 'Non sono figli', 'Non ho bambino', 'Ho non figli'],
                correct: 0,
                explanation: '"Non ho figli" is correct — simple negation with "non" before the verb "ho".',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now talk naturally about children, ages and expecting a baby in Italian. Next up — the difference between friends and family.',
                xp: 15,
            },
        ],
    },
    'friends-vs-family': {
        title: 'Friends vs family',
        topic: 'Family & relationships',
        duration: '5 min',
        xp: 15,
        slides: [
            {
                type: 'intro',
                title: 'Friends who feel like family',
                body: 'Italians often blur the line between close friends and family. This lesson covers the vocabulary for describing friendships and how close bonds are expressed.',
                emoji: '🧑‍🤝‍🧑',
            },
            {
                type: 'vocabulary',
                word: 'L\'amico / l\'amica',
                translation: 'The friend (male/female)',
                phonetic: '/laˈmiːko/',
                note: 'Uses "l\'" before both genders since both start with a vowel.',
                emoji: '🧑',
            },
            {
                type: 'vocabulary',
                word: 'Il migliore amico',
                translation: 'The best friend (male)',
                phonetic: '/il miʎˈʎoːre aˈmiːko/',
                note: '"La migliore amica" for a female best friend — "migliore" (best) doesn\'t change with gender, but the noun does.',
                emoji: '🤝',
            },
            {
                type: 'vocabulary',
                word: 'Un amico di famiglia',
                translation: 'A family friend',
                phonetic: '/un aˈmiːko di faˈmiʎʎa/',
                note: 'Describes someone close to the whole family, not just to you personally.',
                emoji: '👪',
            },
            {
                type: 'vocabulary',
                word: 'È come un fratello per me',
                translation: 'He is like a brother to me',
                phonetic: '/ɛ ˈkome un fraˈtɛllo per me/',
                note: 'A warm, common way to describe an especially close friendship — "come" means "like/as".',
                emoji: '❤️',
            },
            {
                type: 'vocabulary',
                word: 'Ci conosciamo da anni',
                translation: 'We\'ve known each other for years',
                phonetic: '/tʃi konoˈʃaːmo da ˈanni/',
                note: 'From "conoscersi" (to know each other) — describes a long-standing friendship.',
                emoji: '⏳',
            },
            {
                type: 'vocabulary',
                word: 'Siamo amici d\'infanzia',
                translation: 'We are childhood friends',
                phonetic: '/ˈsjaːmo aˈmiːtʃi dinˈfantsja/',
                note: '"Infanzia" means childhood — describes friends known since being very young.',
                emoji: '🧒',
            },
            {
                type: 'vocabulary',
                word: 'Un conoscente',
                translation: 'An acquaintance',
                phonetic: '/un konoˈʃɛnte/',
                note: 'Someone less close than a friend — used to distinguish casual contacts from real friendships.',
                emoji: '🤷',
            },
            {
                type: 'vocabulary',
                word: 'Fidarsi di qualcuno',
                translation: 'To trust someone',
                phonetic: '/fiˈdarsi di kwalˈkuːno/',
                note: '"Mi fido di lui" — I trust him. An essential verb for describing relationships.',
                emoji: '🤝',
            },
            {
                type: 'vocabulary',
                word: 'Restare in contatto',
                translation: 'To stay in touch',
                phonetic: '/resˈtaːre in konˈtatto/',
                note: 'Useful when talking about maintaining friendships, especially at a distance.',
                emoji: '📱',
            },
            {
                type: 'vocabulary',
                word: 'Un\'amicizia speciale',
                translation: 'A special friendship',
                phonetic: '/unamiˈtʃittsja speˈtʃaːle/',
                note: '"L\'amicizia" (friendship) is the noun form of "amico" — used to describe the bond itself.',
                emoji: '✨',
            },
            {
                type: 'question',
                question: 'What does "è come un fratello per me" mean?',
                options: ['He is my actual brother', 'He is like a brother to me', 'He is my cousin', 'He lives with my brother'],
                correct: 1,
                explanation: '"Come" means "like/as" — this expresses a very close friendship, not an actual family relationship.',
            },
            {
                type: 'question',
                question: 'What is "un conoscente"?',
                options: ['A best friend', 'A family member', 'An acquaintance', 'A childhood friend'],
                correct: 2,
                explanation: '"Un conoscente" means an acquaintance — someone you know but aren\'t especially close to.',
            },
            {
                type: 'question',
                question: 'What does "siamo amici d\'infanzia" mean?',
                options: ['We are new friends', 'We are childhood friends', 'We are family friends', 'We are work friends'],
                correct: 1,
                explanation: '"Amici d\'infanzia" means childhood friends — "infanzia" refers to early childhood.',
            },
            {
                type: 'question',
                question: 'How would you say "we\'ve known each other for years"?',
                options: ['Ci vediamo da anni', 'Ci conosciamo da anni', 'Siamo amici da anni', 'Parliamo da anni'],
                correct: 1,
                explanation: '"Ci conosciamo da anni" comes from "conoscersi" (to know each other) — describing a long friendship.',
            },
            {
                type: 'question',
                question: 'What does "fidarsi di qualcuno" mean?',
                options: ['To meet someone', 'To trust someone', 'To call someone', 'To forget someone'],
                correct: 1,
                explanation: '"Fidarsi di" means to trust — "mi fido di lui" means I trust him.',
            },
            {
                type: 'question',
                question: 'What is "un amico di famiglia"?',
                options: ['A relative', 'A family friend', 'A best friend', 'An acquaintance'],
                correct: 1,
                explanation: '"Un amico di famiglia" describes someone close to the whole family, not just one person.',
            },
            {
                type: 'question',
                question: 'What does "restare in contatto" mean?',
                options: ['To stay in touch', 'To visit often', 'To live together', 'To argue'],
                correct: 0,
                explanation: '"Restare in contatto" means to stay in touch — commonly used when discussing maintaining friendships.',
            },
            {
                type: 'question',
                question: 'How would you refer to your best female friend?',
                options: ['Il migliore amico', 'La migliore amica', 'L\'amico speciale', 'La conoscente'],
                correct: 1,
                explanation: '"La migliore amica" is correct — the feminine article and noun both change, while "migliore" stays the same.',
            },
            {
                type: 'question',
                question: 'What does "un\'amicizia speciale" mean?',
                options: ['A special friend', 'A special friendship', 'A special family', 'A special acquaintance'],
                correct: 1,
                explanation: '"L\'amicizia" is the noun for "friendship" itself, not the person — "un\'amicizia speciale" means a special friendship.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now describe different levels of friendship and how they compare to family bonds. Next up — talking about family celebrations.',
                xp: 15,
            },
        ],
    },
    'family-celebrations': {
        title: 'Family celebrations',
        topic: 'Family & relationships',
        duration: '7 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'Celebrating together',
                body: 'Family celebrations are central to Italian culture — birthdays, holidays and reunions bring everyone together. Let\'s learn how to talk about them.',
                emoji: '🎉',
            },
            {
                type: 'vocabulary',
                word: 'Il compleanno',
                translation: 'The birthday',
                phonetic: '/il kompleˈanno/',
                note: '"Buon compleanno!" — Happy birthday! One of the most useful phrases to know.',
                emoji: '🎂',
            },
            {
                type: 'vocabulary',
                word: 'Festeggiare',
                translation: 'To celebrate',
                phonetic: '/festedˈdʒaːre/',
                note: '"Festeggiamo il suo compleanno" — we\'re celebrating his/her birthday.',
                emoji: '🎊',
            },
            {
                type: 'vocabulary',
                word: 'Ci riuniamo tutti insieme',
                translation: 'We all get together',
                phonetic: '/tʃi riuˈnjaːmo ˈtutti inˈsjɛme/',
                note: 'From "riunirsi" (to gather/reunite) — commonly used for family reunions at holidays.',
                emoji: '👨‍👩‍👧‍👦',
            },
            {
                type: 'vocabulary',
                word: 'Il pranzo di Natale',
                translation: 'Christmas lunch',
                phonetic: '/il ˈprantso di naˈtaːle/',
                note: 'A major family event in Italy — often the biggest meal of the year.',
                emoji: '🎄',
            },
            {
                type: 'vocabulary',
                word: 'Passiamo le feste insieme',
                translation: 'We spend the holidays together',
                phonetic: '/pasˈsjaːmo le ˈfɛste inˈsjɛme/',
                note: '"Le feste" refers to the holiday season broadly, especially Christmas and New Year.',
                emoji: '🎁',
            },
            {
                type: 'vocabulary',
                word: 'Un matrimonio',
                translation: 'A wedding',
                phonetic: '/un matriˈmɔːnjo/',
                note: 'A major family celebration in Italian culture, often involving the whole extended family.',
                emoji: '💒',
            },
            {
                type: 'vocabulary',
                word: 'Il battesimo',
                translation: 'The baptism',
                phonetic: '/il batˈtɛːzimo/',
                note: 'A traditional family celebration for a new baby, still very common in Italy.',
                emoji: '👶',
            },
            {
                type: 'vocabulary',
                word: 'Brindiamo!',
                translation: 'Let\'s toast!',
                phonetic: '/brinˈdjaːmo/',
                note: 'Used when raising a glass together — an essential celebration word.',
                emoji: '🥂',
            },
            {
                type: 'vocabulary',
                word: 'Fare gli auguri',
                translation: 'To give good wishes / congratulate',
                phonetic: '/ˈfaːre ʎi auˈɡuːri/',
                note: '"Ti faccio i miei auguri" — I wish you well. Used for birthdays, weddings, and other occasions.',
                emoji: '💐',
            },
            {
                type: 'vocabulary',
                word: 'È una tradizione di famiglia',
                translation: 'It\'s a family tradition',
                phonetic: '/ɛ ˈuna traditˈtsjoːne di faˈmiʎʎa/',
                note: 'Useful for describing recurring customs, like a specific dish made every year.',
                emoji: '📿',
            },
            {
                type: 'question',
                question: 'How do you say "Happy birthday"?',
                options: ['Buon Natale', 'Buon compleanno', 'Buone feste', 'Tanti auguri di matrimonio'],
                correct: 1,
                explanation: '"Buon compleanno" is the standard way to say Happy Birthday in Italian.',
            },
            {
                type: 'question',
                question: 'What does "ci riuniamo tutti insieme" mean?',
                options: ['We argue with each other', 'We all get together', 'We travel separately', 'We celebrate alone'],
                correct: 1,
                explanation: '"Riunirsi" means to gather or reunite — commonly used for family reunions during holidays.',
            },
            {
                type: 'question',
                question: 'What does "il pranzo di Natale" refer to?',
                options: ['A birthday dinner', 'A wedding reception', 'Christmas lunch', 'A baptism celebration'],
                correct: 2,
                explanation: '"Il pranzo di Natale" is Christmas lunch — a major, often elaborate family event in Italy.',
            },
            {
                type: 'question',
                question: 'What does "brindiamo!" mean?',
                options: ['Let\'s eat!', 'Let\'s toast!', 'Let\'s dance!', 'Let\'s go!'],
                correct: 1,
                explanation: '"Brindiamo!" means "let\'s toast!" — used when raising glasses together at a celebration.',
            },
            {
                type: 'question',
                question: 'What does "fare gli auguri" mean?',
                options: ['To plan a party', 'To give good wishes / congratulate', 'To cook a meal', 'To buy a gift'],
                correct: 1,
                explanation: '"Fare gli auguri" means to give good wishes or congratulate someone, used for many occasions.',
            },
            {
                type: 'question',
                question: 'What is "il battesimo"?',
                options: ['The wedding', 'The baptism', 'The birthday party', 'The anniversary'],
                correct: 1,
                explanation: '"Il battesimo" means the baptism — a traditional family celebration for a new baby in Italy.',
            },
            {
                type: 'question',
                question: 'What does "è una tradizione di famiglia" mean?',
                options: ['It\'s a family argument', 'It\'s a family tradition', 'It\'s a family business', 'It\'s a family secret'],
                correct: 1,
                explanation: '"È una tradizione di famiglia" describes a recurring family custom, like a dish made every year.',
            },
            {
                type: 'question',
                question: 'What does "passiamo le feste insieme" mean?',
                options: ['We celebrate separately', 'We spend the holidays together', 'We travel during the holidays', 'We work during the holidays'],
                correct: 1,
                explanation: '"Passiamo le feste insieme" means we spend the holidays together — "le feste" refers to the holiday season.',
            },
            {
                type: 'question',
                question: 'What is "un matrimonio"?',
                options: ['A birthday', 'A wedding', 'A funeral', 'A graduation'],
                correct: 1,
                explanation: '"Un matrimonio" means a wedding — a major family celebration in Italian culture.',
            },
            {
                type: 'complete',
                title: 'Lesson complete!',
                body: 'You can now talk about family celebrations, from birthdays to weddings to holiday traditions. One more lesson to go — describing relationships more deeply.',
                xp: 20,
            },
        ],
    },
    'describing-relationships': {
        title: 'Describing relationships',
        topic: 'Family & relationships',
        duration: '7 min',
        xp: 20,
        slides: [
            {
                type: 'intro',
                title: 'Putting it all together',
                body: 'This final lesson brings together everything you\'ve learned — describing the depth, quality and history of your relationships with family and friends.',
                emoji: '💞',
            },
            {
                type: 'vocabulary',
                word: 'Il rapporto',
                translation: 'The relationship',
                phonetic: '/il rapˈporto/',
                note: '"Abbiamo un buon rapporto" — we have a good relationship. General word for any kind of bond.',
                emoji: '🔗',
            },
            {
                type: 'vocabulary',
                word: 'Un legame forte',
                translation: 'A strong bond',
                phonetic: '/un leˈɡaːme ˈfɔrte/',
                note: '"Legame" literally means "tie" or "bond" — used for describing emotional closeness.',
                emoji: '💪',
            },
            {
                type: 'vocabulary',
                word: 'Prendersi cura di qualcuno',
                translation: 'To take care of someone',
                phonetic: '/ˈprendersi ˈkuːra di kwalˈkuːno/',
                note: 'A reflexive construction — "mi prendo cura di lei" — I take care of her.',
                emoji: '🤲',
            },
            {
                type: 'vocabulary',
                word: 'Sostenersi a vicenda',
                translation: 'To support each other',
                phonetic: '/sosteˈnersi a viˈtʃɛnda/',
                note: '"A vicenda" means "each other/mutually" — describes reciprocal support in a relationship.',
                emoji: '🤝',
            },
            {
                type: 'vocabulary',
                word: 'Volersi bene',
                translation: 'To love/care for each other',
                phonetic: '/voˈlersi ˈbɛne/',
                note: 'A warm, common expression — less intense than "amarsi" (to love romantically), used broadly for family and close friends.',
                emoji: '❤️',
            },
            {
                type: 'vocabulary',
                word: 'Avere alti e bassi',
                translation: 'To have ups and downs',
                phonetic: '/aˈveːre ˈalti e ˈbassi/',
                note: 'A realistic, common phrase — "ogni rapporto ha alti e bassi" (every relationship has ups and downs).',
                emoji: '📉',
            },
            {
                type: 'vocabulary',
                word: 'Riconciliarsi',
                translation: 'To reconcile / make up',
                phonetic: '/rikontʃiˈljarsi/',
                note: 'Used after a disagreement — "ci siamo riconciliati" (we made up).',
                emoji: '🕊️',
            },
            {
                type: 'vocabulary',
                word: 'Perdonare',
                translation: 'To forgive',
                phonetic: '/perdoˈnaːre/',
                note: 'An important verb for describing how families and friendships work through conflict.',
                emoji: '🙏',
            },
            {
                type: 'vocabulary',
                word: 'Un rapporto complicato',
                translation: 'A complicated relationship',
                phonetic: '/un rapˈporto komplikaːto/',
                note: 'Useful for describing relationships that are meaningful but difficult.',
                emoji: '🌀',
            },
            {
                type: 'vocabulary',
                word: 'Non potrei vivere senza di loro',
                translation: 'I couldn\'t live without them',
                phonetic: '/non poˈtrɛi ˈviːvere ˈsentsa di ˈloːro/',
                note: 'An emotional, heartfelt way to express deep importance of family or close friends in your life.',
                emoji: '💕',
            },
            {
                type: 'question',
                question: 'What does "un legame forte" mean?',
                options: ['A weak connection', 'A strong bond', 'A recent friendship', 'A distant relative'],
                correct: 1,
                explanation: '"Un legame forte" means a strong bond — "legame" literally means tie or bond.',
            },
            {
                type: 'question',
                question: 'What does "volersi bene" express?',
                options: ['Romantic love only', 'To love/care for each other (family, friends)', 'To dislike someone', 'To forget someone'],
                correct: 1,
                explanation: '"Volersi bene" is a warm expression used broadly for family and close friends — less intense than romantic "amarsi".',
            },
            {
                type: 'question',
                question: 'What does "sostenersi a vicenda" mean?',
                options: ['To ignore each other', 'To support each other', 'To argue constantly', 'To live far apart'],
                correct: 1,
                explanation: '"A vicenda" means "each other/mutually" — "sostenersi a vicenda" means to support each other.',
            },
            {
                type: 'question',
                question: 'What does "avere alti e bassi" mean?',
                options: ['To be always happy', 'To have ups and downs', 'To be very tall', 'To travel frequently'],
                correct: 1,
                explanation: '"Avere alti e bassi" literally means "to have highs and lows" — a realistic phrase about relationships.',
            },
            {
                type: 'question',
                question: 'What does "riconciliarsi" mean?',
                options: ['To argue', 'To reconcile / make up', 'To separate', 'To celebrate'],
                correct: 1,
                explanation: '"Riconciliarsi" means to reconcile or make up, typically used after a disagreement.',
            },
            {
                type: 'question',
                question: 'What does "perdonare" mean?',
                options: ['To forget', 'To forgive', 'To punish', 'To apologize'],
                correct: 1,
                explanation: '"Perdonare" means to forgive — an important verb for describing how relationships work through conflict.',
            },
            {
                type: 'question',
                question: 'What does "prendersi cura di qualcuno" mean?',
                options: ['To avoid someone', 'To take care of someone', 'To criticize someone', 'To forget someone'],
                correct: 1,
                explanation: '"Prendersi cura di" is a reflexive construction meaning to take care of someone.',
            },
            {
                type: 'question',
                question: 'What does "un rapporto complicato" describe?',
                options: ['A simple relationship', 'A complicated relationship', 'A new relationship', 'A distant relationship'],
                correct: 1,
                explanation: '"Un rapporto complicato" describes a relationship that is meaningful but difficult.',
            },
            {
                type: 'question',
                question: 'What does "non potrei vivere senza di loro" mean?',
                options: ['I could live without them easily', 'I couldn\'t live without them', 'I don\'t know them well', 'I rarely see them'],
                correct: 1,
                explanation: 'This is an emotional expression of deep importance — "I couldn\'t live without them".',
            },
            {
                type: 'question',
                question: 'What is "il rapporto"?',
                options: ['A specific family member', 'The general word for a relationship or bond', 'A type of celebration', 'A wedding ceremony'],
                correct: 1,
                explanation: '"Il rapporto" is the general word for any kind of relationship — "abbiamo un buon rapporto" means we have a good relationship.',
            },
            {
                type: 'complete',
                title: 'Family & relationships — complete!',
                body: 'You\'ve finished all 10 lessons in this topic! You can now discuss every part of family life in Italian — from immediate family to describing the depth of your closest relationships.',
                xp: 20,
            },
        ],
    },
};

export const lessonsItalian = {
    en: italianEnglish,
};